/** @format */

// src/components/products/ProductForm.jsx

import {
  faAward,
  faBook,
  faBoxArchive,
  faCalculator,
  faCamera,
  faCartShopping,
  faChevronDown,
  faChevronUp,
  faDollarSign,
  faExternalLinkAlt,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faPenToSquare,
  faSearch,
  faSync,
  faToggleOn,
  faTrash,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { api } from '../../../../api.ts';
import { selectAuth } from '../../../../redux/authSlice';
import { moneyFormatter, vatFormatter } from '../../../../utils/numbers';
import { sortBy } from '../../../../utils/sort';
import CustomAutocomplete from '../../../misc/CustomAutocomplete';
import MultiSelectAutocomplete from '../../../misc/MultiSelectAutocomplete';
import NewTooltip from '../../../misc/NewTooltip';
import FileBrowserDialog from './FileBrowserDialog';
import ProductContainingKitsPanel from './ProductContainingKitsPanel';
import ProductSearchAutocomplete from './ProductSearchAutocomplete';
import { formatApiError } from './productName';

const initialFormData = {
  public_name: '',
  internal_name: '',
  visual_name: '',
  force_quote_description: false,
  quote_description: '',
  internal_note: '',
  study_description: '',
  study_name: '',
  brand: '',
  supplier: '',
  supplier_price: 0,
  is_active: false,
  is_validated: false,
  can_be_bought: false,
  can_be_sold: false,
  sub_categories: '',
  typology_slug: '',
};
// detailsData will be separate
// const initialDetailsData = {};

// Mapping from model/field name to endpoint and label field
const RELATION_FIELD_MAP = {
  typology: { endpoint: '/products/v2/typologies/', labelField: 'name' },
  related_typology: {
    endpoint: '/products/v2/typologies/',
    labelField: 'name',
  },
  brand: { endpoint: '/products/v2/brands/', labelField: 'name' },
  supplier: { endpoint: '/products/v2/suppliers/', labelField: 'name' },
  // Add more as needed
};

function getRelationConfig(fieldSchema) {
  // Try to infer model name from field name
  const modelKey = fieldSchema.name.replace(/_id$/, '');
  if (RELATION_FIELD_MAP[modelKey]) {
    return RELATION_FIELD_MAP[modelKey];
  }
  // Fallback: try pluralizing
  if (RELATION_FIELD_MAP[`${modelKey}s`]) {
    return RELATION_FIELD_MAP[`${modelKey}s`];
  }
  // Fallback: guess endpoint
  return {
    endpoint: `/api/${modelKey.replace(/_/g, '-')}/`,
    labelField: 'name',
  };
}

// Centralized payload builder for submit and preview
function buildProductPayload({
  formData,
  detailsData,
  detailsSchema,
  detailsFieldKey,
  includeIsValidated = true,
  formulas = {},
}) {
  // Helper to extract the primary key from a related object (slug or id)
  function getPrimaryKey(obj) {
    if (!obj) return null;
    if (typeof obj === 'object') {
      if ('slug' in obj) return obj.slug;
      if ('id' in obj) return obj.id;
    }
    return obj;
  }

  // Build detailsToSend with PK extraction only for relation/foreignkey fields
  let detailsToSend = { ...detailsData };
  if (detailsSchema && detailsSchema.fields) {
    detailsToSend = { ...detailsToSend };
    detailsSchema.fields.forEach((field) => {
      const { name, fieldType, relatedModel } = field;
      if (
        fieldType === 'relation' ||
        fieldType === 'foreignkey' ||
        relatedModel
      ) {
        detailsToSend[name] = getPrimaryKey(detailsData[name]);
      }
    });
  }
  delete detailsToSend.product;
  delete detailsToSend.product_id;

  const payload = {
    ...formData,
    is_active: formData.is_archived ? false : formData.is_active,
    is_visible_in_offer: formData.is_archived
      ? false
      : formData.is_visible_in_offer,
    is_visible_in_catalog: formData.is_visible_in_catalog || false,
    brand: formData.brand ? formData.brand.id : null,
    custom_quote_description: formData.quote_description,
    custom_study_description: formData.study_description,
    sub_categories: Array.isArray(formData.sub_categories)
      ? formData.sub_categories.map((sc) => sc.id || sc.slug)
      : [],
    supplier_price: formData.supplier_price,
    category_id: formData.category_id
      ? parseInt(String(formData.category_id), 10)
      : null,
    sub_category_id: formData.sub_category_id
      ? parseInt(String(formData.sub_category_id), 10)
      : null,
    typology: formData.typology_slug || null,
    data_sheets: formData.data_sheets || [],
    warranties: formData.warranties || [],
    conformity_certificates: formData.conformity_certificates || [],
    user_manuals: formData.user_manuals || [],
    ...(detailsFieldKey ? { [detailsFieldKey]: detailsToSend } : {}),
  };

  if (!includeIsValidated) {
    delete payload.is_validated;
  }

  // Remove generated fields if they are generated (formula present and is_validated)
  // Note: internal_name and public_name are read-only in the backend
  // We always need to send custom_internal_name and custom_public_name for user edits
  if (formulas.public_name && formData.is_validated) {
    // Product is validated with formula - backend will generate it, don't send custom value
    delete payload.public_name;
  } else {
    // Either no formula OR not validated - send as custom field
    payload.custom_public_name = payload.public_name;
    delete payload.public_name;
  }
  if (formulas.internal_name && formData.is_validated) {
    // Product is validated with formula - backend will generate it, don't send custom value
    delete payload.internal_name;
  } else {
    // Either no formula OR not validated - send as custom field
    payload.custom_internal_name = payload.internal_name;
    delete payload.internal_name;
  }
  if (
    formulas.quote_description &&
    formData.is_validated &&
    !formData.force_quote_description
  ) {
    delete payload.custom_quote_description;
  }
  if (formulas.study_description && formData.is_validated) {
    delete payload.custom_study_description;
  }
  if (formulas.study_name && formData.is_validated) {
    delete payload.study_name;
  } else {
    payload.custom_study_name = payload.study_name;
    delete payload.study_name;
  }

  delete payload.quote_description;
  delete payload.study_description;
  return payload;
}

// Helper to map subcategory IDs to objects
function mapSubCategoryIdsToObjects(ids, allSubCategories) {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((idOrObj) => {
      if (typeof idOrObj === 'object' && idOrObj !== null) return idOrObj;
      return allSubCategories.find((sc) => sc.id === idOrObj) || null;
    })
    .filter(Boolean);
}

// Helper to clean and parse supplier price as float
const cleanSupplierPrice = (value) => {
  if (typeof value === 'string') {
    return parseFloat(value.replace(',', '.').replace(/\s/g, ''));
  }
  return Number(value);
};

// Helper to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Helper to calculate price with VAT
const getVatPrices = (basePrice) => {
  if (!basePrice || Number.isNaN(basePrice)) return [];
  const rates = [5.5, 10, 20];
  return rates.map((rate) => ({
    rate,
    price: basePrice * (1 + rate / 100),
  }));
};

function ProductForm({
  open,
  onClose,
  onSave,
  product,
  isLoading: isSavingProduct,
  handleDuplicateProduct,
  handleSyncProductToOdoo,
  readOnly = false,
  onStartEdit,
}) {
  const formDisabled = readOnly || isSavingProduct;
  const [formData, setFormData] = useState(initialFormData);
  const [detailsData, setDetailsData] = useState({});

  const [categories, setCategories] = useState([]); // Use Category[] if typed
  const [subCategories, setSubCategories] = useState([]); // Use SubCategory[]
  const [typologies, setTypologies] = useState([]); // Use Typology[]
  const [detailsSchema, setDetailsSchema] = useState(null);
  const [loadingDetailsSchema, setLoadingDetailsSchema] = useState(false);

  const [loadingTaxonomies, setLoadingTaxonomies] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [detailsFieldKey, setDetailsFieldKey] = useState(null);

  const detailsDataString = useMemo(
    () => JSON.stringify(detailsData),
    [detailsData],
  );

  const [formulas, setFormulas] = useState({});
  const [generatedNames, setGeneratedNames] = useState({});
  const [previewFields, setPreviewFields] = useState({});
  const [formulaTexts, setFormulaTexts] = useState({});

  // Add useMemo for subCategoriesString:
  const subCategoriesString = useMemo(
    () => JSON.stringify(formData.sub_categories),
    [formData.sub_categories],
  );

  const [kitLines, setKitLines] = useState(product?.kit_lines || []);
  const isKit = kitLines.length > 0 || product?.is_kit;
  const [productDialogTab, setProductDialogTab] = useState('product');
  const [containingKitsCount, setContainingKitsCount] = useState(null);
  const [containingKitsSyncing, setContainingKitsSyncing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProductDialogTab('product');
    setContainingKitsCount(null);
  }, [open, product?.id]);

  const handleContainingKitsCount = useCallback((count) => {
    setContainingKitsCount(count);
  }, []);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    product?.image_url || product?.image || null,
  );
  const [dataSheets, setDataSheets] = useState(
    product?.data_sheets?.map((sheet) => ({
      id: sheet.id,
      name: sheet.name,
      file: sheet.file,
      url: sheet.url,
    })) || [],
  );
  const [originalDataSheets, setOriginalDataSheets] = useState(
    product?.data_sheets?.map((sheet) => sheet.id) || [],
  );
  const [dataSheetFiles, setDataSheetFiles] = useState({});
  const [dataSheetBrowserOpen, setDataSheetBrowserOpen] = useState(false);

  const [warranties, setWarranties] = useState(
    product?.warranties?.map((warranty) => ({
      id: warranty.id,
      name: warranty.name,
      file: warranty.file,
      url: warranty.url,
    })) || [],
  );
  const [originalWarranties, setOriginalWarranties] = useState(
    product?.warranties?.map((warranty) => warranty.id) || [],
  );
  const [warrantyFiles, setWarrantyFiles] = useState({});
  const [warrantyBrowserOpen, setWarrantyBrowserOpen] = useState(false);

  const [conformityCertificates, setConformityCertificates] = useState(
    product?.conformity_certificates?.map((cert) => ({
      id: cert.id,
      name: cert.name,
      file: cert.file,
      url: cert.url,
    })) || [],
  );
  const [originalConformityCertificates, setOriginalConformityCertificates] =
    useState(product?.conformity_certificates?.map((cert) => cert.id) || []);
  const [conformityCertificateFiles, setConformityCertificateFiles] = useState(
    {},
  );
  const [
    conformityCertificateBrowserOpen,
    setConformityCertificateBrowserOpen,
  ] = useState(false);

  const [userManuals, setUserManuals] = useState(
    product?.user_manuals?.map((manual) => ({
      id: manual.id,
      name: manual.name,
      file: manual.file,
      url: manual.url,
    })) || [],
  );
  const [originalUserManuals, setOriginalUserManuals] = useState(
    product?.user_manuals?.map((manual) => manual.id) || [],
  );
  const [userManualFiles, setUserManualFiles] = useState({});
  const [userManualBrowserOpen, setUserManualBrowserOpen] = useState(false);

  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceFormula, setPriceFormula] = useState('');

  // Add state for typology margin
  const [typologyMargin, setTypologyMargin] = useState(null);

  const [yearlyDegradationDialogOpen, setYearlyDegradationDialogOpen] =
    useState(false);
  const [degradationDialogCapacity, setDegradationDialogCapacity] =
    useState('');
  const [degradationDialogDuration, setDegradationDialogDuration] =
    useState('');
  /** Shown below the field after a successful dialog apply (not persisted to API). */
  const [yearlyDegradationMeta, setYearlyDegradationMeta] = useState(null);

  // --- Kit line prices state ---
  const [kitLinePrices, setKitLinePrices] = useState([]);
  const [kitLineLoading, setKitLineLoading] = useState([]);
  const [kitLineErrors, setKitLineErrors] = useState([]);

  /** Abort in-flight fetches when the user changes kit line product quickly. */
  const kitLineChildDocFetchAbortRef = useRef({});

  const [calculatedDescription, setCalculatedDescription] = useState('');

  const { user } = useSelector(selectAuth);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // Flatten nested errors for use in helperText
  const flattenErrors = (obj, prefix = '') =>
    Object.entries(obj).reduce((acc, [key, val]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (Array.isArray(val)) {
        acc[newKey] = val;
      } else if (typeof val === 'object' && val !== null) {
        Object.assign(acc, flattenErrors(val, newKey));
      }
      return acc;
    }, {});

  // Fetch taxonomies using your api.ts
  useEffect(() => {
    if (open) {
      setLoadingTaxonomies(true);
      setFormError(null);
      setDetailsSchema(null);
      Promise.all([
        api.get('/products/v2/categories/'), // Adjust endpoint if needed; add <Category[]> type if api.ts supports it
        api.get('/products/v2/typologies/'), // Add <Typology[]>
        api.get('/products/v2/sub-categories/'), // Add <SubCategory[]>
      ])
        .then(([catRes, typoRes, subCatRes]) => {
          setCategories(catRes.data.results || catRes.data || []);
          setTypologies(typoRes.data.results || typoRes.data || []);
          setSubCategories(subCatRes.data.results || subCatRes.data || []);
        })
        .catch((err) => {
          console.error('Failed to load taxonomies', err);
          const errorMsg =
            err.response?.data?.detail ||
            err.message ||
            'Failed to load required selection data.';
          setFormError(errorMsg);
        })
        .finally(() => {
          setLoadingTaxonomies(false);
        });
    }
  }, [open]);

  // Populate form if editing
  useEffect(() => {
    const prodLocal1 = product;
    if (prodLocal1 && open) {
      setFormData({
        public_name: prodLocal1.public_name || '',
        internal_name: prodLocal1.internal_name || '',
        visual_name: prodLocal1.visual_name || '',
        quote_description: prodLocal1.quote_description || '',
        internal_note: prodLocal1.internal_note || '',
        study_description: prodLocal1.study_description || '',
        study_name: prodLocal1.study_name || '',
        brand: prodLocal1.brand || null,
        supplier: prodLocal1.supplier || '',
        supplier_price: prodLocal1.supplier_price || 0,
        is_active:
          prodLocal1.is_active !== undefined ? prodLocal1.is_active : true,
        is_validated:
          prodLocal1.is_validated !== undefined
            ? prodLocal1.is_validated
            : true,
        is_visible_in_offer:
          prodLocal1.is_visible_in_offer !== undefined
            ? prodLocal1.is_visible_in_offer
            : true,
        is_archived:
          prodLocal1.is_archived !== undefined ? prodLocal1.is_archived : false,
        is_visible_in_catalog:
          prodLocal1.is_visible_in_catalog !== undefined
            ? prodLocal1.is_visible_in_catalog
            : false,
        // Always store sub_categories as array of objects
        sub_categories: mapSubCategoryIdsToObjects(
          prodLocal1.sub_categories || [],
          subCategories,
        ),
        typology_slug: prodLocal1.typology?.slug || '',
        force_quote_description: prodLocal1.force_quote_description || false,
        price_formula: prodLocal1.price_formula || '',
        odoo_id: prodLocal1.odoo_id || null,
        can_be_bought:
          prodLocal1.can_be_bought !== undefined
            ? prodLocal1.can_be_bought
            : true,
        can_be_sold:
          prodLocal1.can_be_sold !== undefined ? prodLocal1.can_be_sold : true,
      });
      // Use the first non-null *_details object
      const detailsObj =
        Object.entries(prodLocal1).find(
          ([key, value]) => key.endsWith('_details') && value != null,
        )?.[1] || {};
      setDetailsData(detailsObj);
      setYearlyDegradationMeta(null);
      if (!prodLocal1.typology?.slug) {
        // If no slug could be determined
        setDetailsSchema(null);
      }
    } else if (!prodLocal1 && open) {
      setFormData(initialFormData);
      setDetailsData({});
      setYearlyDegradationMeta(null);
      setDetailsSchema(null);
    }
    if (!open) {
      setFormError(null);
    }
  }, [product, open, typologies]); // Add typologies dependency for when they load after product

  // Fetch formulas when product loads or typology changes
  useEffect(() => {
    // Always fetch from typology if we have a typology_slug
    // This ensures we get both the formula existence (boolean) and the formula text
    if (formData.typology_slug) {
      api
        .get(`/products/v2/typologies/${formData.typology_slug}/`)
        .then((res) => {
          const formulaFields = [
            'public_name',
            'internal_name',
            'prefix_internal_name',
            'quote_description',
            'study_description',
            'study_name',
          ];
          const formulasObj = {};
          const formulaTextsObj = {};
          formulaFields.forEach((field) => {
            formulasObj[field] = Boolean(res.data[`${field}_formula`]);
            formulaTextsObj[field] = res.data[`${field}_formula`] || '';
          });
          setFormulas(formulasObj);
          setFormulaTexts(formulaTextsObj);
        })
        .catch(() => {
          setFormulas({});
          setFormulaTexts({});
        });
    } else {
      setFormulas({});
      setFormulaTexts({});
    }
  }, [product, formData.typology_slug]);

  // Function to fetch preview of generated fields without validation
  const fetchPreviewFields = useCallback(
    ({ typologySlug, kitLinesArg = [] }) => {
      if (!typologySlug) {
        setPreviewFields({});
        return;
      }

      // Build the payload for the new preview_generated_fields endpoint
      const details = detailsDataString ? JSON.parse(detailsDataString) : {};
      const dataToSend = {
        typology: typologySlug,
        public_name: formData.public_name,
        internal_name: formData.internal_name,
        visual_name: formData.visual_name,
        custom_quote_description: formData.quote_description,
        study_description: formData.study_description,
        study_name: formData.study_name,
        brand: formData.brand ? formData.brand.id : null,
        supplier_price: cleanSupplierPrice(formData.supplier_price) || 0,
        force_quote_description: formData.force_quote_description,
        sub_categories: Array.isArray(formData.sub_categories)
          ? formData.sub_categories.map((sc) => sc.id || sc.slug)
          : [],
      };

      // Add details if present
      if (detailsFieldKey && details) {
        dataToSend[detailsFieldKey] = details;
      }

      // Add kit lines to the payload if present
      if (kitLinesArg.length > 0) {
        dataToSend.kit_lines = kitLinesArg.map((line) => ({
          ...(line.id && { id: line.id }),
          product:
            line.product && line.product.id ? line.product.id : line.product,
          quantity: line.quantity,
          show_on_quote: line.show_on_quote,
          order: line.order || 0,
        }));
      }

      api
        .post(
          '/products/v2/admin-products/preview_generated_fields/',
          {},
          dataToSend,
        )
        .then((res) => {
          setPreviewFields({
            internal_name: decodeHtmlEntities(res.data.internal_name || ''),
            public_name: decodeHtmlEntities(res.data.public_name || ''),
            prefix_internal_name: decodeHtmlEntities(
              res.data.prefix_internal_name || '',
            ),
            quote_description: decodeHtmlEntities(
              res.data.quote_description || '',
            ),
            study_description: decodeHtmlEntities(
              res.data.study_description || '',
            ),
            study_name: decodeHtmlEntities(res.data.study_name || ''),
          });
        })
        .catch((error) => {
          console.error('Preview fields error:', error);
          setPreviewFields({});
        });
    },
    [
      formData,
      detailsDataString,
      detailsFieldKey,
      formData.public_name,
      formData.internal_name,
      formData.quote_description,
      formData.study_description,
      formData.study_name,
      formData.brand,
      formData.supplier_price,
      formData.force_quote_description,
      formData.sub_categories,
    ],
  );

  // Combined function that fetches both generated names and calculated price
  const fetchDynamicPreview = useCallback(
    ({
      supplierPrice,
      typologySlug,
      formulaForCalc,
      publicName,
      quoteDescription,
      productId,
      kitLinesArg = [],
    }) => {
      // Default to 0 if supplierPrice is empty, undefined, null, or not a number
      let cleanedPrice = cleanSupplierPrice(supplierPrice);
      if (
        cleanedPrice === '' ||
        cleanedPrice === undefined ||
        cleanedPrice === null ||
        Number.isNaN(cleanedPrice)
      ) {
        cleanedPrice = 0;
      }
      if (!typologySlug) {
        return;
      }

      // Build the payload for the new dynamic_preview endpoint
      const details = detailsDataString ? JSON.parse(detailsDataString) : {};
      const dataToSend = buildProductPayload({
        formData: {
          ...formData,
          supplier_price: cleanedPrice,
          price_formula: formulaForCalc,
        },
        detailsData: details,
        detailsSchema,
        detailsFieldKey,
        includeIsValidated: true,
        formulas,
      });

      // Add kit lines to the payload if present
      if (kitLinesArg.length > 0) {
        dataToSend.kit_lines = kitLinesArg.map((line) => ({
          ...(line.id && { id: line.id }), // Include id if it exists
          product:
            line.product && line.product.id ? line.product.id : line.product,
          quantity: line.quantity,
          show_on_quote: line.show_on_quote,
          order: line.order || 0, // Include order field
        }));
      }

      // Add products array for price calculation (empty for now)
      dataToSend.products = [];

      api
        .post('/products/v2/admin-products/dynamic_preview/', {}, dataToSend)
        .then((res) => {
          // Update generated names with HTML entity decoding
          setGeneratedNames({
            internal_name: decodeHtmlEntities(res.data.internal_name),
            public_name: decodeHtmlEntities(res.data.public_name),
            prefix_internal_name: decodeHtmlEntities(
              res.data.prefix_internal_name,
            ),
            quote_description: decodeHtmlEntities(res.data.quote_description),
            study_description: decodeHtmlEntities(res.data.study_description),
            study_name: decodeHtmlEntities(res.data.study_name),
          });

          // Update calculated price and description
          setCalculatedPrice(res.data.price);
          setCalculatedDescription(
            decodeHtmlEntities(res.data.description || ''),
          );

          // Clear field errors
          setFieldErrors({});
        })
        .catch((error) => {
          console.error('Dynamic preview error:', error);
          setCalculatedPrice(null);
          setCalculatedDescription('');
        });
    },
    [formData, detailsDataString, detailsSchema, detailsFieldKey, formulas],
  );

  // Live preview: call backend to get preview of generated fields
  // This useEffect handles updates when form data changes with debouncing
  useEffect(() => {
    if (!open || !formData.typology_slug) {
      setPreviewFields({});
      return undefined;
    }

    const debounceTimeout = setTimeout(() => {
      fetchPreviewFields({
        typologySlug: formData.typology_slug,
        kitLinesArg: kitLines,
      });
    }, 500); // Debounce for 500ms

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [
    open,
    formData.typology_slug,
    formData.public_name,
    formData.internal_name,
    formData.force_quote_description,
    formData.quote_description,
    formData.study_description,
    formData.study_name,
    formData.brand,
    formData.supplier_price,
    detailsDataString,
    detailsFieldKey,
    subCategoriesString,
    JSON.stringify(kitLines),
    fetchPreviewFields,
  ]);

  // Live preview: call backend to get generated names and calculated price
  // This useEffect handles updates when form data changes with debouncing
  useEffect(() => {
    if (!open || !formData.typology_slug) {
      return () => {};
    }

    // Skip if this is the initial load (handled by the popup open useEffect)
    if (open && product && !formData.public_name && !formData.internal_name) {
      return () => {};
    }

    const debounceTimeout = setTimeout(() => {
      const payload = {
        supplierPrice: formData.supplier_price,
        typologySlug: formData.typology_slug,
        formulaForCalc: formData.price_formula,
        publicName: formData.public_name || product?.public_name,
        quoteDescription:
          formData.quote_description || product?.quote_description,
        productId: product?.id,
        kitLinesArg: kitLines,
      };

      fetchDynamicPreview(payload);
    }, 300); // Debounce for 300ms

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [
    open,
    formData.typology_slug,
    formData.public_name,
    formData.internal_name,
    formData.visual_name,
    formData.force_quote_description,
    formData.quote_description,
    formData.study_description,
    formData.study_name,
    formData.brand,
    formData.supplier_price,
    formData.price_formula,
    detailsDataString,
    detailsSchema,
    detailsFieldKey,
    formulas,
    subCategoriesString,
    JSON.stringify(kitLines),
    fetchDynamicPreview,
  ]);

  // Effect to fetch details schema when typology_id (and thus slug) changes
  useEffect(() => {
    if (formData.typology_slug && open) {
      setLoadingDetailsSchema(true);
      setDetailsSchema(null); // Clear previous schema first
      api
        .options(
          `/products/v2/admin-products/?typology_id=${formData.typology_slug}`,
        )
        .then((response) => {
          const actions = response.data?.actions?.POST;
          if (actions) {
            // Find the details field (e.g., 'inverter_details', 'solar_panel_details', etc.)
            const foundDetailsFieldKey = Object.keys(actions).find(
              (key) =>
                key.endsWith('_details') &&
                actions[key].type === 'nested object',
            );
            setDetailsFieldKey(foundDetailsFieldKey || null);
            if (foundDetailsFieldKey) {
              const detailsChildren = actions[foundDetailsFieldKey].children;
              // Convert to array of fields
              const fields = Object.entries(detailsChildren).map(
                ([name, field]) => {
                  let inferredFieldType;
                  if (field.type !== 'string') {
                    inferredFieldType = field.type;
                  } else if (field.max_length && field.max_length > 200) {
                    inferredFieldType = 'text';
                  } else {
                    inferredFieldType = 'string';
                  }
                  return {
                    name,
                    label: field.label || name,
                    fieldType: inferredFieldType,
                    required: field.required,
                    helpText: field.help_text,
                    maxLength: field.max_length,
                    choices: field.choices,
                    defaultValue: field.default, // DRF may not provide this, so may be undefined
                    readOnly: field.read_only,
                    relatedModel: field.related_model,
                    relatedEndpoint: field.related_endpoint,
                  };
                },
              );
              setDetailsSchema({
                typology_name: actions[foundDetailsFieldKey].label,
                fields,
              });
              // Robustly initialize detailsData for both new and existing products
              const initialDetails = {};
              fields.forEach((field) => {
                let value = '';
                if (
                  product &&
                  foundDetailsFieldKey &&
                  product[foundDetailsFieldKey] &&
                  product[foundDetailsFieldKey][field.name] !== undefined &&
                  product[foundDetailsFieldKey][field.name] !== null
                ) {
                  value = product[foundDetailsFieldKey][field.name];
                  initialDetails[field.name] = value;
                } else if (field.defaultValue !== undefined) {
                  value = field.defaultValue;
                  initialDetails[field.name] = value;
                } else if (field.fieldType === 'boolean') {
                  value = false;
                  initialDetails[field.name] = value;
                } else if (field.relatedModel) {
                  value = null;
                  initialDetails[field.name] = value;
                } else {
                  initialDetails[field.name] = value;
                }
              });
              setDetailsData(initialDetails);
              setYearlyDegradationMeta(null);
              return;
            }
          }
          // fallback if no details field found
          setDetailsFieldKey(null);
          setDetailsSchema({ fields: [] });
        })
        .catch((err) => {
          console.error(
            `Failed to load details schema for ${formData.typology_slug}`,
            err,
          );
          const errorMsg =
            err.response?.data?.detail ||
            err.message ||
            `Could not load form fields for ${formData.typology_slug}.`;
          setFormError((prevError) =>
            prevError ? `${prevError}\n${errorMsg}` : errorMsg,
          );
          setDetailsSchema({ fields: [] }); // Set to empty fields to prevent breaking map
        })
        .finally(() => {
          setLoadingDetailsSchema(false);
        });
    } else {
      setDetailsSchema(null); // Clear schema if no typology selected
    }
  }, [formData.typology_slug, open, product]); // Add 'product' to re-init details for existing products

  // Auto-set register_needed when brand has registration_for_extended_guarantee
  useEffect(() => {
    const { brand } = formData;
    const hasRegisterNeededField = detailsSchema?.fields?.some(
      (f) => f.name === 'register_needed',
    );
    if (hasRegisterNeededField) {
      setDetailsData((prev) => ({
        ...prev,
        register_needed: brand?.registration_for_extended_guarantee,
      }));
    }
  }, [formData.brand, detailsSchema?.fields]);

  useEffect(() => {
    const prodLocal2 = product;
    if (prodLocal2 && prodLocal2.is_kit && prodLocal2.id) {
      setKitLines(prodLocal2.kit_lines || []);
    } else if (prodLocal2 && prodLocal2.is_kit && !prodLocal2.id) {
      setKitLines(prodLocal2.kit_lines || []);
    }
  }, [product]);

  // Fetch typology price formula and margin when typology changes or dialog opens with a product
  useEffect(() => {
    const slug = formData.typology_slug || product?.typology?.slug;
    if (slug) {
      api
        .get(`/products/v2/typologies/${slug}/`)
        .then((res) => {
          setPriceFormula(formData.price_formula || res.data.price_formula);
          setTypologyMargin(
            res.data.margin !== undefined && res.data.margin !== null
              ? res.data.margin
              : null,
          );
        })
        .catch(() => {
          setPriceFormula('');
          setTypologyMargin(null);
        });
    } else {
      setPriceFormula('');
      setTypologyMargin(null);
    }
  }, [formData.typology_slug, product?.typology?.slug, open]);

  // Trigger dynamic preview calculation on popup open for existing products
  useEffect(() => {
    if (
      open &&
      product && // Only for existing products
      formData.typology_slug &&
      formData.supplier_price !== undefined &&
      formData.supplier_price !== null
    ) {
      // Immediate call for popup open (no debouncing)
      fetchDynamicPreview({
        supplierPrice: formData.supplier_price,
        typologySlug: formData.typology_slug,
        formulaForCalc: formData.price_formula,
        publicName: formData.public_name,
        quoteDescription: formData.quote_description,
        productId: product.id,
        kitLinesArg: kitLines,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only depend on open to avoid conflicts with other useEffects

  // Helper to fetch price for a kit line
  const fetchKitLinePrice = useCallback(async (productId, quantity, idx) => {
    if (!productId || !quantity || quantity < 1) return;
    setKitLineLoading((prev) => {
      const arr = [...prev];
      arr[idx] = true;
      return arr;
    });
    setKitLineErrors((prev) => {
      const arr = [...prev];
      arr[idx] = null;
      return arr;
    });
    try {
      const res = await api.post(
        '/products/v2/admin-products/kit_line_price/',
        {},
        { product_id: productId, quantity },
      );
      setKitLinePrices((prev) => {
        const arr = [...prev];
        arr[idx] = res.data.price;
        return arr;
      });
    } catch (err) {
      setKitLineErrors((prev) => {
        const arr = [...prev];
        arr[idx] = 'Erreur';
        return arr;
      });
      setKitLinePrices((prev) => {
        const arr = [...prev];
        arr[idx] = null;
        return arr;
      });
    } finally {
      setKitLineLoading((prev) => {
        const arr = [...prev];
        arr[idx] = false;
        return arr;
      });
    }
  }, []);

  // Effect: fetch price for each kit line when product or quantity changes
  useEffect(() => {
    kitLines.forEach((line, idx) => {
      const productId = line.product && line.product.id;
      const { quantity } = line;
      if (productId && quantity && quantity > 0) {
        fetchKitLinePrice(productId, quantity, idx);
      } else {
        setKitLinePrices((prev) => {
          const arr = [...prev];
          arr[idx] = null;
          return arr;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(kitLines)]);

  const kitLinesSorted = useMemo(
    () => [...kitLines].sort(sortBy('order')),
    [kitLines],
  );

  const kitLinesPriceTotal = useMemo(() => {
    if (!kitLinesSorted.length) return null;
    for (let i = 0; i < kitLinesSorted.length; i += 1) {
      if (kitLineLoading[i]) return { status: 'loading' };
    }
    for (let i = 0; i < kitLinesSorted.length; i += 1) {
      if (kitLineErrors[i]) return { status: 'incomplete' };
      if (kitLinePrices[i] === undefined || kitLinePrices[i] === null) {
        return { status: 'incomplete' };
      }
    }
    const total = kitLinesSorted.reduce(
      (sum, _, idx) => sum + Number(kitLinePrices[idx]),
      0,
    );
    return { status: 'ok', total };
  }, [kitLinesSorted, kitLinePrices, kitLineLoading, kitLineErrors]);

  let odooChipLabelId;
  if (product && product.id && product.is_validated) {
    if (product.is_kit) {
      odooChipLabelId = product.odoo_bom_id;
    } else if (product.odoo_id) {
      odooChipLabelId = product.odoo_id;
    } else {
      odooChipLabelId = 'N/A';
    }
  }

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'typology_slug') {
      // Always reset the form to initial state, but keep the new typology_slug
      setFormData({
        ...initialFormData,
        typology_slug: value,
      });
      setDetailsData({});
      setYearlyDegradationMeta(null);
      setDetailsSchema(null);
      // setKitLines([]);
      setFormulas({});
      setGeneratedNames({});
      setFieldErrors({});
      setFormError(null);
      return; // Prevent further updates for this event
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'category') {
      setFormData((prev) => ({ ...prev, sub_category_id: '' }));
    }
  };

  const handlePriceChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDetailsChange = (eventOrPath, valueOrEvent) => {
    // Handles both direct value changes and event objects
    if (typeof eventOrPath === 'string') {
      // For custom components like Switch that pass name and checked status
      const name = eventOrPath;
      const val = valueOrEvent;
      setDetailsData((prev) => ({ ...prev, [name]: val }));
    } else if (eventOrPath && eventOrPath.target) {
      // Standard event object
      const { name, value, type, checked } = eventOrPath.target;
      setDetailsData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' || type === 'switch' ? checked : value,
      }));
    } else if (
      eventOrPath &&
      typeof eventOrPath === 'object' &&
      'name' in eventOrPath
    ) {
      // CustomAutocomplete onChange: { target: { name, value } }
      const { name, value } = eventOrPath.target;
      setDetailsData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCloseYearlyDegradationDialog = () => {
    setYearlyDegradationDialogOpen(false);
  };

  const handleApplyYearlyDegradation = () => {
    const cap = parseFloat(
      String(degradationDialogCapacity).replace(',', '.'),
      10,
    );
    const dur = parseFloat(
      String(degradationDialogDuration).replace(',', '.'),
      10,
    );
    if (!Number.isFinite(dur) || dur <= 0) {
      enqueueSnackbar('Indiquez une durée de garantie valide (années).', {
        variant: 'warning',
      });
      return;
    }
    if (!Number.isFinite(cap)) {
      enqueueSnackbar('Indiquez une capacité garantie valide (%).', {
        variant: 'warning',
      });
      return;
    }
    if (cap >= 100) {
      enqueueSnackbar('La capacité garantie doit être inférieure à 100 %.', {
        variant: 'warning',
      });
      return;
    }
    const yearly = (100 - cap) / dur;
    const rounded = Math.round(yearly * 10000) / 10000;
    handleDetailsChange({
      target: { name: 'yearly_degradation', value: String(rounded) },
    });
    setYearlyDegradationMeta({
      capacityPct: degradationDialogCapacity.trim(),
      durationYears: degradationDialogDuration.trim(),
    });
    setYearlyDegradationDialogOpen(false);
  };

  const handleDataSheetChange = (index, field, value) => {
    setDataSheets((prev) =>
      prev.map((sheet, i) =>
        i === index ? { ...sheet, [field]: value } : sheet,
      ),
    );
  };

  const handleDataSheetFileChange = (index, file) => {
    setDataSheetFiles((prev) => ({
      ...prev,
      [index]: file,
    }));
  };

  const removeDataSheet = (index) => {
    setDataSheets((prev) => prev.filter((_, i) => i !== index));
    setDataSheetFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const openDataSheet = (sheet) => {
    if (sheet.url) {
      window.open(sheet.url, '_blank');
    }
  };

  const handleDataSheetBrowserSelect = (selectedDataSheets) => {
    // Add the selected data sheets to the current list
    const newDataSheets = selectedDataSheets.map((sheet) => ({
      id: sheet.id,
      name: sheet.name,
      file: sheet.file,
      url: sheet.url,
    }));

    // Merge with existing data sheets, avoiding duplicates
    const existingIds = dataSheets.map((sheet) => sheet.id);
    const uniqueNewDataSheets = newDataSheets.filter(
      (sheet) => !existingIds.includes(sheet.id),
    );

    setDataSheets((prev) => [...prev, ...uniqueNewDataSheets]);
  };

  const handleWarrantyChange = (index, field, value) => {
    setWarranties((prev) =>
      prev.map((warranty, i) =>
        i === index ? { ...warranty, [field]: value } : warranty,
      ),
    );
  };

  const handleWarrantyFileChange = (index, file) => {
    setWarrantyFiles((prev) => ({
      ...prev,
      [index]: file,
    }));
  };

  const removeWarranty = (index) => {
    setWarranties((prev) => prev.filter((_, i) => i !== index));
    setWarrantyFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const openWarranty = (warranty) => {
    if (warranty.url) {
      window.open(warranty.url, '_blank');
    }
  };

  const handleWarrantyBrowserSelect = (selectedWarranties) => {
    // Add the selected warranties to the current list
    const newWarranties = selectedWarranties.map((warranty) => ({
      id: warranty.id,
      name: warranty.name,
      file: warranty.file,
      url: warranty.url,
    }));

    // Merge with existing warranties, avoiding duplicates
    const existingIds = warranties.map((warranty) => warranty.id);
    const uniqueNewWarranties = newWarranties.filter(
      (warranty) => !existingIds.includes(warranty.id),
    );

    setWarranties((prev) => [...prev, ...uniqueNewWarranties]);
  };

  const handleConformityCertificateChange = (index, field, value) => {
    setConformityCertificates((prev) =>
      prev.map((cert, i) => (i === index ? { ...cert, [field]: value } : cert)),
    );
  };

  const handleConformityCertificateFileChange = (index, file) => {
    setConformityCertificateFiles((prev) => ({
      ...prev,
      [index]: file,
    }));
  };

  const removeConformityCertificate = (index) => {
    setConformityCertificates((prev) => prev.filter((_, i) => i !== index));
    setConformityCertificateFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const openConformityCertificate = (cert) => {
    if (cert.url) {
      window.open(cert.url, '_blank');
    }
  };

  const handleConformityCertificateBrowserSelect = (selectedCerts) => {
    // Add the selected conformity certificates to the current list
    const newCerts = selectedCerts.map((cert) => ({
      id: cert.id,
      name: cert.name,
      file: cert.file,
      url: cert.url,
    }));

    // Merge with existing conformity certificates, avoiding duplicates
    const existingIds = conformityCertificates.map((cert) => cert.id);
    const uniqueNewCerts = newCerts.filter(
      (cert) => !existingIds.includes(cert.id),
    );

    setConformityCertificates((prev) => [...prev, ...uniqueNewCerts]);
  };

  const handleUserManualChange = (index, field, value) => {
    setUserManuals((prev) =>
      prev.map((manual, i) =>
        i === index ? { ...manual, [field]: value } : manual,
      ),
    );
  };

  const handleUserManualFileChange = (index, file) => {
    setUserManualFiles((prev) => ({
      ...prev,
      [index]: file,
    }));
  };

  const removeUserManual = (index) => {
    setUserManuals((prev) => prev.filter((_, i) => i !== index));
    setUserManualFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const openUserManual = (manual) => {
    if (manual.url) {
      window.open(manual.url, '_blank');
    }
  };

  const handleUserManualBrowserSelect = (selectedManuals) => {
    const newManuals = selectedManuals.map((manual) => ({
      id: manual.id,
      name: manual.name,
      file: manual.file,
      url: manual.url,
    }));

    const existingIds = userManuals.map((manual) => manual.id);
    const uniqueNewManuals = newManuals.filter(
      (manual) => !existingIds.includes(manual.id),
    );

    setUserManuals((prev) => [...prev, ...uniqueNewManuals]);
  };

  const mergeDocumentsFromKitChildProduct = useCallback(
    async (kitLineIndex, childProductId) => {
      if (!childProductId) return;
      if (product?.id && childProductId === product.id) return;

      const prevAbort = kitLineChildDocFetchAbortRef.current[kitLineIndex];
      if (prevAbort) prevAbort.abort();
      const ac = new AbortController();
      kitLineChildDocFetchAbortRef.current[kitLineIndex] = ac;

      const mapDoc = (item) => ({
        id: item.id,
        name: item.name,
        file: item.file,
        url: item.url,
      });

      try {
        const { data: child } = await api.get(
          `/products/v2/admin-products/${childProductId}/`,
          {},
          {},
          'json',
          { signal: ac.signal },
        );

        setDataSheets((prev) => {
          const ids = new Set(prev.map((x) => x.id).filter(Boolean));
          const add = (child.data_sheets || [])
            .map(mapDoc)
            .filter((x) => x.id && !ids.has(x.id));
          return add.length ? [...prev, ...add] : prev;
        });
        setWarranties((prev) => {
          const ids = new Set(prev.map((x) => x.id).filter(Boolean));
          const add = (child.warranties || [])
            .map(mapDoc)
            .filter((x) => x.id && !ids.has(x.id));
          return add.length ? [...prev, ...add] : prev;
        });
        setConformityCertificates((prev) => {
          const ids = new Set(prev.map((x) => x.id).filter(Boolean));
          const add = (child.conformity_certificates || [])
            .map(mapDoc)
            .filter((x) => x.id && !ids.has(x.id));
          return add.length ? [...prev, ...add] : prev;
        });
        setUserManuals((prev) => {
          const ids = new Set(prev.map((x) => x.id).filter(Boolean));
          const add = (child.user_manuals || [])
            .map(mapDoc)
            .filter((x) => x.id && !ids.has(x.id));
          return add.length ? [...prev, ...add] : prev;
        });
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        console.error('Failed to load kit child product documents:', e);
      }
    },
    [product?.id],
  );

  useEffect(
    () => () => {
      Object.values(kitLineChildDocFetchAbortRef.current).forEach((c) =>
        c?.abort(),
      );
    },
    [],
  );

  const handleDuplicate = async () => {
    if (!product || !product.id) return;

    setFormError(null);

    try {
      await handleDuplicateProduct(product);
      onClose();
    } catch (err) {
      setFormError('Erreur lors de la duplication du produit.');
      console.error('Duplicate product error:', err);
    }
  };

  // Helper functions to reorder kit lines
  const moveKitLineUp = (index) => {
    if (index === 0) return; // Can't move first item up

    setKitLines((prevKitLines) => {
      const sortedLines = prevKitLines.sort(sortBy('order'));
      const newLines = [...sortedLines];
      [newLines[index - 1], newLines[index]] = [
        newLines[index],
        newLines[index - 1],
      ];

      // Update order values to maintain consistency
      return newLines.map((line, i) => ({ ...line, order: i + 1 }));
    });
  };

  const moveKitLineDown = (index) => {
    const currentSortedLines = kitLines.sort(sortBy('order'));
    if (index === currentSortedLines.length - 1) return; // Can't move last item down

    setKitLines((prevKitLines) => {
      const sortedLines = prevKitLines.sort(sortBy('order'));
      const newLines = [...sortedLines];
      [newLines[index], newLines[index + 1]] = [
        newLines[index + 1],
        newLines[index],
      ];

      // Update order values to maintain consistency
      return newLines.map((line, i) => ({ ...line, order: i + 1 }));
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (readOnly) return;
    setFormError(null);
    setFieldErrors({});
    if (!formData.typology_slug) {
      setFormError('Veuillez sélectionner une typologie.');
      return;
    }

    const isStrictSave = !!formData.is_validated;

    if (isStrictSave) {
      if (!formData.sub_categories || formData.sub_categories.length === 0) {
        setFieldErrors((prev) => ({
          ...prev,
          sub_categories: [
            'Veuillez sélectionner au moins une sous-catégorie.',
          ],
        }));
        setFormError('Veuillez sélectionner au moins une sous-catégorie.');
        return;
      }
    }

    // Check if we need to fill fields with formulas for new products
    const isNewProduct = !product?.id;
    const fieldsToCheck = [
      { key: 'public_name', label: 'Nom public' },
      { key: 'internal_name', label: 'Nom interne' },
      { key: 'quote_description', label: 'Description devis' },
      { key: 'study_description', label: 'Description étude' },
      { key: 'study_name', label: 'Nom étude' },
    ];

    // For new products, fill empty fields that have formulas
    const fieldsToAutoFill = {};
    // Check if quote_description will be generated from kit lines
    const hasKitLines = kitLines.length > 0;
    const quoteDescFromKitLines =
      hasKitLines && !formData.force_quote_description;

    if (isStrictSave) {
      const emptyFieldWithoutFormula = fieldsToCheck.find((field) => {
        // Study name and description stay optional on validated products.
        if (field.key === 'study_description' || field.key === 'study_name') {
          return false;
        }

        // Skip quote_description if it will be generated from kit lines,
        // or when the product is an accessory (description is optional).
        if (
          field.key === 'quote_description' &&
          (quoteDescFromKitLines || formData.typology_slug === 'accessories')
        ) {
          return false;
        }

        const hasFormula = formulas[field.key];
        const fieldValue = formData[field.key];
        const isEmpty = !fieldValue || fieldValue.trim() === '';
        // hasFormula will be true if formula exists, false if no formula, undefined if not loaded
        // We only block if explicitly false (no formula exists)
        return isEmpty && hasFormula === false;
      });

      if (emptyFieldWithoutFormula) {
        setFieldErrors((prev) => ({
          ...prev,
          [emptyFieldWithoutFormula.key]: [
            `Le champ "${emptyFieldWithoutFormula.label}" est vide et n'a pas de formule. Veuillez le remplir manuellement.`,
          ],
        }));
        setFormError(
          `Le champ "${emptyFieldWithoutFormula.label}" est vide et n'a pas de formule. Veuillez le remplir manuellement.`,
        );
        return;
      }

      const missingRequiredDetail = detailsSchema?.fields?.find((field) => {
        if (!field.required || field.readOnly) {
          return false;
        }
        if (field.fieldType === 'boolean') {
          return false;
        }
        const fieldValue = detailsData[field.name];
        if (fieldValue === 0) {
          return false;
        }
        if (fieldValue && typeof fieldValue === 'object') {
          return false;
        }
        return (
          fieldValue === undefined || fieldValue === null || fieldValue === ''
        );
      });
      if (missingRequiredDetail) {
        const detailsErrorKey = detailsFieldKey
          ? `${detailsFieldKey}.${missingRequiredDetail.name}`
          : missingRequiredDetail.name;
        setFieldErrors((prev) => ({
          ...prev,
          [detailsErrorKey]: ['Ce champ est obligatoire.'],
        }));
        setFormError(
          `Le champ "${missingRequiredDetail.label}" est obligatoire.`,
        );
        return;
      }
    }

    if (isNewProduct) {
      // Collect fields to auto-fill in the payload (don't modify formData)
      fieldsToCheck.forEach((field) => {
        // Skip quote_description if it will be generated from kit lines
        if (field.key === 'quote_description' && quoteDescFromKitLines) {
          return;
        }

        const hasFormula = formulas[field.key];
        const fieldValue = formData[field.key];
        const isEmpty = !fieldValue || fieldValue.trim() === '';

        if (isEmpty && hasFormula && previewFields[field.key]) {
          // Mark this field to be filled in the payload
          fieldsToAutoFill[field.key] = previewFields[field.key];
        }
      });
    }

    // Handle data sheets - do this BEFORE buildProductPayload
    const hasNewDataSheets = Object.keys(dataSheetFiles).length > 0;
    const existingDataSheets = dataSheets.filter((sheet) => sheet.id);
    const newDataSheets = dataSheets.filter(
      (sheet) =>
        !sheet.id && sheet.name && dataSheetFiles[dataSheets.indexOf(sheet)],
    );

    // Get current data sheet IDs (only the ones still in the UI)
    const currentDataSheetIds = existingDataSheets.map((sheet) => sheet.id);

    // Set data sheets in formData so it gets included in the payload
    formData.data_sheets = currentDataSheetIds;

    // Handle warranties - do this BEFORE buildProductPayload
    const hasNewWarranties = Object.keys(warrantyFiles).length > 0;
    const existingWarranties = warranties.filter((warranty) => warranty.id);
    const newWarranties = warranties.filter(
      (warranty) =>
        !warranty.id &&
        warranty.name &&
        warrantyFiles[warranties.indexOf(warranty)],
    );

    // Get current warranty IDs (only the ones still in the UI)
    const currentWarrantyIds = existingWarranties.map(
      (warranty) => warranty.id,
    );

    // Set warranties in formData so it gets included in the payload
    formData.warranties = currentWarrantyIds;

    // Handle conformity certificates - do this BEFORE buildProductPayload
    const hasNewConformityCertificates =
      Object.keys(conformityCertificateFiles).length > 0;
    const existingConformityCertificates = conformityCertificates.filter(
      (cert) => cert.id,
    );
    const newConformityCertificates = conformityCertificates.filter(
      (cert) =>
        !cert.id &&
        cert.name &&
        conformityCertificateFiles[conformityCertificates.indexOf(cert)],
    );

    // Get current conformity certificate IDs (only the ones still in the UI)
    const currentConformityCertificateIds = existingConformityCertificates.map(
      (cert) => cert.id,
    );

    // Set conformity_certificates in formData so it gets included in the payload
    formData.conformity_certificates = currentConformityCertificateIds;

    // Handle user manuals - do this BEFORE buildProductPayload
    const existingUserManuals = userManuals.filter((manual) => manual.id);
    const currentUserManualIds = existingUserManuals.map((manual) => manual.id);
    formData.user_manuals = currentUserManualIds;

    const payload = buildProductPayload({
      formData,
      detailsData,
      detailsSchema,
      detailsFieldKey,
      includeIsValidated: true,
      formulas,
    });

    // Add auto-filled fields to payload for new products
    if (isNewProduct && fieldsToAutoFill) {
      // Map formData keys to payload keys
      // Note: buildProductPayload already converted these based on formulas
      // We need to set the fields that buildProductPayload expects
      if (fieldsToAutoFill.public_name) {
        // If there's a formula for new validated products, set public_name
        // Otherwise it was already converted to custom_public_name
        if (formulas.public_name && formData.is_validated) {
          payload.public_name = fieldsToAutoFill.public_name;
        } else {
          payload.custom_public_name = fieldsToAutoFill.public_name;
        }
      }
      if (fieldsToAutoFill.internal_name) {
        if (formulas.internal_name && formData.is_validated) {
          payload.internal_name = fieldsToAutoFill.internal_name;
        } else {
          payload.custom_internal_name = fieldsToAutoFill.internal_name;
        }
      }
      if (fieldsToAutoFill.quote_description) {
        payload.custom_quote_description = fieldsToAutoFill.quote_description;
      }
      if (fieldsToAutoFill.study_description) {
        payload.custom_study_description = fieldsToAutoFill.study_description;
      }
      if (fieldsToAutoFill.study_name) {
        payload.custom_study_name = fieldsToAutoFill.study_name;
      }
    }

    payload.quote_description = calculatedDescription;

    // Handle kit_lines: always include them in payload regardless of force_quote_description
    if (kitLines.length > 0) {
      // Only delete custom_quote_description if force_quote_description is false
      if (!formData.force_quote_description) {
        delete payload.custom_quote_description;
      }

      const getKitLineProductId = (prod) => (prod && prod.id ? prod.id : null);
      payload.kit_lines = kitLines
        .map(
          ({
            id,
            product: prod,
            quantity,
            show_on_quote: showOnQuote,
            order,
          }) => ({
            ...(id && { id }), // Include id if it exists (for updates)
            product: getKitLineProductId(prod),
            quantity,
            show_on_quote: showOnQuote,
            order: order || 0, // Include order field
          }),
        )
        .filter((line) => line.product); // Only include lines with a valid product id
    } else {
      payload.kit_lines = [];
    }
    payload.supplier_price = cleanSupplierPrice(payload.supplier_price);
    if (Number.isNaN(payload.supplier_price) || payload.supplier_price === '') {
      payload.supplier_price = 0;
    }

    // Prepare data sheet upload data if there are new files
    let dataSheetFormData = null;
    if (hasNewDataSheets) {
      dataSheetFormData = new FormData();

      // Add new data sheet files
      newDataSheets.forEach((sheet, index) => {
        const fileIndex = dataSheets.indexOf(sheet);
        const file = dataSheetFiles[fileIndex];
        if (file) {
          dataSheetFormData.append(`data_sheet_name_${index}`, sheet.name);
          dataSheetFormData.append(`data_sheet_file_${index}`, file);
        }
      });
    }

    // Prepare warranty upload data if there are new files
    let warrantyFormData = null;
    if (hasNewWarranties) {
      warrantyFormData = new FormData();

      // Add new warranty files
      newWarranties.forEach((warranty, index) => {
        const fileIndex = warranties.indexOf(warranty);
        const file = warrantyFiles[fileIndex];
        if (file) {
          warrantyFormData.append(`warranty_name_${index}`, warranty.name);
          warrantyFormData.append(`warranty_file_${index}`, file);
        }
      });
    }

    try {
      // Always send product data as JSON payload
      // Pass data sheet FormData and warranty FormData separately for conditional upload
      await onSave(
        payload,
        imageFile,
        product?.id,
        dataSheetFormData,
        warrantyFormData,
      );
    } catch (error) {
      console.error('Save product error:', error);
      const errData = error.response?.data;
      if (errData && typeof errData === 'object') {
        setFieldErrors(flattenErrors(errData));
        setFormError(
          formatApiError(
            error,
            'Une erreur est survenue lors de la sauvegarde.',
          ),
        );
      } else {
        setFormError('Une erreur est survenue lors de la sauvegarde.');
      }
    }
  };

  // Helper function to render a single dynamic field
  const renderDynamicDetailField = (fieldSchema) => {
    const {
      name,
      label,
      fieldType,
      required: fieldRequired,
      choices,
      helpText,
      maxLength,
      readOnly: fieldReadOnly,
      relatedModel, // if present in schema
    } = fieldSchema;
    const value = detailsData[name] !== undefined ? detailsData[name] : '';
    const required = !!fieldRequired && !!formData.is_validated;

    // Detect relation fields
    const isRelation =
      fieldType === 'relation' ||
      fieldType === 'foreignkey' ||
      fieldType === 'field' ||
      (name.endsWith('_id') && !choices) ||
      (relatedModel && typeof relatedModel === 'string');

    // For details fields, use the key `${detailsFieldKey}.${name}`
    const detailsErrorKey = detailsFieldKey
      ? `${detailsFieldKey}.${name}`
      : name;

    if (isRelation) {
      // Use backend-provided endpoint if available
      const endpoint =
        fieldSchema.relatedEndpoint || getRelationConfig(fieldSchema).endpoint;
      return (
        <CustomAutocomplete
          key={name}
          endpoint={(q) =>
            `${endpoint}${q ? `?search=${encodeURIComponent(q)}` : ''}`
          }
          getOptionLabel={(option) => option?.name || option?.label || ''}
          getOptionKey={(option) => option.slug || option.id}
          isOptionEqualToValue={(o, v) => (o.slug || o.id) === (v.slug || v.id)}
          name={name}
          label={label}
          value={value}
          onChange={(e, newValue) => {
            handleDetailsChange({
              target: { name, value: newValue || null },
            });
          }}
          required={required}
          disabled={fieldReadOnly || formDisabled || !formData.typology_slug}
          fullWidth
          helperText={
            helpText ||
            (fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0])
          }
          error={!!fieldErrors[detailsErrorKey]}
          allowNull={!required}
        />
      );
    }
    switch (fieldType) {
      case 'string':
      case 'text': // Assuming 'text' is for multiline
        return (
          <TextField
            key={name}
            name={name}
            label={label}
            value={value}
            onChange={handleDetailsChange}
            fullWidth
            required={required}
            margin="dense"
            variant="outlined"
            multiline={fieldType === 'text'}
            rows={fieldType === 'text' ? 3 : 1}
            helperText={
              helpText ||
              (fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0])
            }
            error={!!fieldErrors[detailsErrorKey]}
            inputProps={{ maxLength }}
            disabled={formDisabled || !formData.typology_slug}
          />
        );
      case 'integer':
      case 'decimal': {
        if (name === 'yearly_degradation' && fieldType === 'decimal') {
          const fieldBlocked = formDisabled || !formData.typology_slug;
          const errMsg =
            fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0];
          const durNum = yearlyDegradationMeta
            ? parseFloat(
                String(yearlyDegradationMeta.durationYears).replace(',', '.'),
                10,
              )
            : NaN;
          const ansWord =
            Number.isFinite(durNum) && durNum === 1 ? 'an' : 'ans';
          return (
            <FormControl
              key={name}
              fullWidth
              required={required}
              margin="dense"
              variant="outlined"
              error={!!fieldErrors[detailsErrorKey]}
            >
              <InputLabel shrink htmlFor="yearly-degradation-input">
                {label}
              </InputLabel>
              <OutlinedInput
                id="yearly-degradation-input"
                name={name}
                type="number"
                value={value}
                label={label}
                disabled
                notched
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip title="Calculer la dégradation annuelle">
                      <span>
                        <IconButton
                          edge="end"
                          size="large"
                          onClick={() => {
                            if (yearlyDegradationMeta) {
                              setDegradationDialogCapacity(
                                yearlyDegradationMeta.capacityPct,
                              );
                              setDegradationDialogDuration(
                                yearlyDegradationMeta.durationYears,
                              );
                            } else {
                              setDegradationDialogCapacity('');
                              setDegradationDialogDuration('');
                            }
                            setYearlyDegradationDialogOpen(true);
                          }}
                          disabled={fieldBlocked}
                          aria-label="Calculer la dégradation annuelle"
                          color="primary"
                        >
                          <FontAwesomeIcon
                            icon={faCalculator}
                            size="md"
                            style={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                }
                inputProps={{
                  step: '0.0001',
                  tabIndex: -1,
                }}
                sx={(th) => ({
                  '&.Mui-disabled': {
                    pointerEvents: 'none',
                    '& .MuiInputAdornment-root': {
                      pointerEvents: 'auto',
                    },
                    '& fieldset': {
                      borderColor: th.palette.action.disabledBackground,
                    },
                    WebkitTextFillColor: th.palette.text.secondary,
                    bgcolor:
                      th.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.06)'
                        : 'rgba(255, 255, 255, 0.09)',
                  },
                })}
              />
              <FormHelperText component="div">
                {helpText || errMsg}
                {yearlyDegradationMeta && (
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    sx={{ mt: helpText || errMsg ? 0.5 : 0 }}
                  >
                    Source du calcul : capacité garantie{' '}
                    {yearlyDegradationMeta.capacityPct}
                    %, durée garantie {yearlyDegradationMeta.durationYears}{' '}
                    {ansWord}
                  </Typography>
                )}
              </FormHelperText>
            </FormControl>
          );
        }
        return (
          <TextField
            key={name}
            name={name}
            label={label}
            type="number"
            value={value}
            onChange={handleDetailsChange}
            fullWidth
            required={required}
            margin="dense"
            variant="outlined"
            helperText={
              helpText ||
              (fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0])
            }
            error={!!fieldErrors[detailsErrorKey]}
            inputProps={{ step: fieldType === 'decimal' ? '0.01' : '1' }} // Adjust step as needed
            disabled={formDisabled || !formData.typology_slug}
          />
        );
      }
      case 'boolean':
        return (
          <FormControlLabel
            key={name}
            control={
              <Switch
                checked={Boolean(detailsData[name])} // Ensure boolean value
                onChange={(e) => handleDetailsChange(name, e.target.checked)} // Pass name and checked status
                name={name}
                disabled={formDisabled || !formData.typology_slug}
              />
            }
            label={label}
            sx={{ mt: 1, mb: 3, display: 'block' }} // Adjust styling as needed
            error={!!fieldErrors[detailsErrorKey]}
            helperText={
              fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0]
            }
          />
        );
      case 'choice':
        return (
          <TextField
            key={name}
            select
            name={name}
            label={label}
            value={value}
            onChange={handleDetailsChange}
            fullWidth
            required={required}
            margin="dense"
            variant="outlined"
            helperText={
              helpText ||
              (fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0])
            }
            error={!!fieldErrors[detailsErrorKey]}
            disabled={formDisabled || !formData.typology_slug}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {choices &&
              choices.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.display_name}
                </MenuItem>
              ))}
          </TextField>
        );
      case 'date': // Example for date
        return (
          <TextField
            key={name}
            name={name}
            label={label}
            type="date"
            value={value} // Ensure YYYY-MM-DD format
            onChange={handleDetailsChange}
            fullWidth
            required={required}
            margin="dense"
            variant="outlined"
            helperText={
              helpText ||
              (fieldErrors[detailsErrorKey] && fieldErrors[detailsErrorKey][0])
            }
            error={!!fieldErrors[detailsErrorKey]}
            InputLabelProps={{ shrink: true }}
            disabled={formDisabled || !formData.typology_slug}
          />
        );
      default:
        return (
          <Typography key={name} variant="caption" color="error">
            Unsupported field type: {fieldType} for &quot;{name}&quot;
          </Typography>
        );
    }
  };

  // Before the return statement in the component, define helperText variables for all fields with nested ternaries:
  const isGeneratedPublicName = Boolean(
    formData.is_validated && formulas && formulas.public_name,
  );
  const isGeneratedInternalName = Boolean(
    formData.is_validated && formulas && formulas.internal_name,
  );

  const isQuoteDescriptionGeneratedFromKitLines = Boolean(
    kitLines.length > 0 && !formData.force_quote_description,
  );

  const isGeneratedQuoteDescription = Boolean(
    (formData.is_validated && formulas && formulas.quote_description) ||
      isQuoteDescriptionGeneratedFromKitLines,
  );
  const isGeneratedStudyDescription = Boolean(
    formData.is_validated && formulas && formulas.study_description,
  );
  const isGeneratedStudyName = Boolean(
    formData.is_validated && formulas && formulas.study_name,
  );

  // Helper to get dialog title based on product type and id
  function getDialogTitle(prod) {
    if (kitLines.length > 0 || (prod && prod.is_kit)) {
      return prod && prod.id ? 'Modifier le kit' : 'Créer un kit';
    }
    return prod && prod.id ? 'Modifier le produit' : 'Créer un nouveau produit';
  }

  const handleSyncToOdoo = async () => {
    if (!product || !product.id) return;

    try {
      await handleSyncProductToOdoo(product);
    } catch (err) {
      console.error('Error during sync:', err);
    }
  };

  if (loadingTaxonomies && open && !categories.length && !typologies.length) {
    // Show loader only if data isn't already partially loaded
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <Typography variant="h5" component="span">
            {product ? 'Modifier le produit' : 'Créer un nouveau produit'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const displaySupplierPrice = cleanSupplierPrice(formData.supplier_price);
  const hasValidSupplierPrice =
    displaySupplierPrice !== null && !Number.isNaN(displaySupplierPrice);
  const isFranchiseeUser = user?.company?.is_franchisee || user?.is_superuser;
  const containingKitProductName =
    product?.prefixed_internal_name ||
    product?.internal_name ||
    product?.custom_internal_name ||
    product?.public_name ||
    (product?.id ? `Produit #${product.id}` : '');
  const kitsTabLabel =
    containingKitsCount == null ? 'Kits' : `Kits (${containingKitsCount})`;
  const showKitsTab = Boolean(product?.id);
  const handleDialogClose = () => {
    if (containingKitsSyncing) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h5" component="span">
            {getDialogTitle(product)}
          </Typography>
          {product && product?.id && (
            <Typography variant="body1" component="p">
              Créé par : {product?.created_by?.full_name}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Peut être acheté ?">
              <ToggleButton
                value="can_be_bought"
                selected={!!formData.can_be_bought}
                onChange={() => {
                  const newValue = !formData.can_be_bought;
                  setFormData((prev) => ({ ...prev, can_be_bought: newValue }));
                }}
                color={formData.can_be_bought ? 'primary' : 'standard'}
                disabled={formDisabled || !formData.typology_slug}
                sx={{ px: 2 }}
              >
                <FontAwesomeIcon icon={faCartShopping} size="lg" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Peut être vendu ?">
              <ToggleButton
                value="can_be_sold"
                selected={!!formData.can_be_sold}
                onChange={() => {
                  const newValue = !formData.can_be_sold;
                  setFormData((prev) => ({ ...prev, can_be_sold: newValue }));
                }}
                color={formData.can_be_sold ? 'primary' : 'standard'}
                disabled={
                  formDisabled ||
                  !formData.typology_slug ||
                  (!user?.is_superuser && !user?.is_product_manager_admin)
                }
                sx={{ px: 2 }}
              >
                <FontAwesomeIcon icon={faDollarSign} size="lg" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Actif ?">
              <ToggleButton
                value="is_active"
                selected={!!formData.is_active}
                onChange={() => {
                  const newValue = !formData.is_active;
                  setFormData((prev) => ({ ...prev, is_active: newValue }));
                }}
                color={formData.is_active ? 'primary' : 'standard'}
                disabled={formDisabled || !formData.typology_slug}
                sx={{ px: 2 }}
              >
                <FontAwesomeIcon icon={faToggleOn} size="lg" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Validé ?">
              <ToggleButton
                value="is_validated"
                selected={!!formData.is_validated}
                onChange={() => {
                  const newValue = !formData.is_validated;
                  setFormData((prev) => ({ ...prev, is_validated: newValue }));
                }}
                color={formData.is_validated ? 'primary' : 'standard'}
                disabled={
                  formDisabled ||
                  !formData.typology_slug ||
                  (!user?.is_superuser && !user?.is_product_manager_admin)
                }
                sx={{ px: 2 }}
              >
                <FontAwesomeIcon icon={faAward} size="lg" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Visible dans les offres ?">
              <ToggleButton
                value="is_visible_in_offer"
                selected={formData.is_visible_in_offer}
                onChange={() => {
                  const newValue = !formData.is_visible_in_offer;
                  setFormData((prev) => ({
                    ...prev,
                    is_visible_in_offer: newValue,
                  }));
                }}
                color={formData.is_visible_in_offer ? 'primary' : 'error'}
                disabled={formDisabled || !formData.typology_slug}
                sx={{
                  px: 2,
                  '&.MuiToggleButton-root': !formData.is_visible_in_offer
                    ? {
                        color: 'error.main',
                      }
                    : {},
                }}
              >
                <FontAwesomeIcon
                  icon={formData.is_visible_in_offer ? faEye : faEyeSlash}
                  size="lg"
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Visible dans le catalogue ?">
              <ToggleButton
                value="is_visible_in_catalog"
                selected={formData.is_visible_in_catalog}
                onChange={() => {
                  const newValue = !formData.is_visible_in_catalog;
                  setFormData((prev) => ({
                    ...prev,
                    is_visible_in_catalog: newValue,
                  }));
                }}
                color={formData.is_visible_in_catalog ? 'primary' : 'error'}
                disabled={
                  formDisabled ||
                  !formData.typology_slug ||
                  (!user?.is_superuser && !user?.is_product_manager_admin)
                }
                sx={{
                  px: 2,
                  '&.MuiToggleButton-root': !formData.is_visible_in_catalog
                    ? {
                        color: 'error.main',
                      }
                    : {},
                }}
              >
                <FontAwesomeIcon icon={faBook} size="lg" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Archivé ?">
              <ToggleButton
                value="is_archived"
                selected={!!formData.is_archived}
                onChange={() => {
                  const newValue = !formData.is_archived;
                  setFormData((prev) => ({ ...prev, is_archived: newValue }));
                }}
                color={formData.is_archived ? 'primary' : 'standard'}
                disabled={formDisabled || !formData.typology_slug}
                sx={{ px: 2 }}
              >
                <FontAwesomeIcon icon={faBoxArchive} size="lg" />
              </ToggleButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      {showKitsTab && (
        <Tabs
          value={productDialogTab}
          onChange={(_, value) => setProductDialogTab(value)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="product"
            label="Produit"
            disabled={containingKitsSyncing}
          />
          <Tab
            value="kits"
            label={kitsTabLabel}
            disabled={containingKitsSyncing}
          />
        </Tabs>
      )}
      <DialogContent dividers>
        <Box
          sx={{
            display:
              showKitsTab && productDialogTab === 'kits' ? 'none' : 'flex',
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          {/* Preview Card - Left Column (Sticky) */}
          <Box
            sx={{
              width: '350px',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            <Card
              sx={{
                bgcolor: 'secondary.lighter',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: 'black', fontWeight: 600 }}
                >
                  Aperçu des champs générés
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  Cet aperçu montre ce que les formules de typologie
                  généreraient avec les détails actuellement remplis.
                </Typography>
              </Box>
              {!formData.typology_slug ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Veuillez d&apos;abord sélectionner une typologie
                  </Typography>
                </Box>
              ) : (
                <List sx={{ pt: 0 }}>
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Nom public
                            </Typography>
                            {formulaTexts.public_name && (
                              <NewTooltip
                                content={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {formulaTexts.public_name}
                                  </Typography>
                                }
                                placement="right"
                                light
                                width={800}
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  size="xs"
                                  style={{ cursor: 'help' }}
                                />
                              </NewTooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          formulaTexts.public_name
                            ? previewFields.public_name
                            : 'Aucune formule existante pour ce champ'
                        }
                        secondaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: formulaTexts.public_name ? 600 : 400,
                          color: formulaTexts.public_name
                            ? 'text.primary'
                            : 'text.secondary',
                          sx: { whiteSpace: 'pre-wrap' },
                        }}
                      />
                    </ListItem>
                  </>
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Nom interne
                            </Typography>
                            {formulaTexts.internal_name && (
                              <NewTooltip
                                content={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {formulaTexts.internal_name}
                                  </Typography>
                                }
                                placement="right"
                                light
                                width={800}
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  size="xs"
                                  style={{ cursor: 'help' }}
                                />
                              </NewTooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          formulaTexts.internal_name
                            ? previewFields.internal_name
                            : 'Aucune formule existante pour ce champ'
                        }
                        secondaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: formulaTexts.internal_name ? 600 : 400,
                          color: formulaTexts.internal_name
                            ? 'text.primary'
                            : 'text.secondary',
                          sx: { whiteSpace: 'pre-wrap' },
                        }}
                      />
                    </ListItem>
                  </>
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Description devis
                            </Typography>
                            {formulaTexts.quote_description && (
                              <NewTooltip
                                content={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {formulaTexts.quote_description}
                                  </Typography>
                                }
                                placement="right"
                                light
                                width={800}
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  size="xs"
                                  style={{ cursor: 'help' }}
                                />
                              </NewTooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          formulaTexts.quote_description
                            ? previewFields.quote_description
                            : 'Aucune formule existante pour ce champ'
                        }
                        secondaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: formulaTexts.quote_description
                            ? 600
                            : 400,
                          color: formulaTexts.quote_description
                            ? 'text.primary'
                            : 'text.secondary',
                          sx: { whiteSpace: 'pre-wrap' },
                        }}
                      />
                    </ListItem>
                  </>
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Description étude
                            </Typography>
                            {formulaTexts.study_description && (
                              <NewTooltip
                                content={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {formulaTexts.study_description}
                                  </Typography>
                                }
                                placement="right"
                                light
                                width={800}
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  size="xs"
                                  style={{ cursor: 'help' }}
                                />
                              </NewTooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          formulaTexts.study_description
                            ? previewFields.study_description
                            : 'Aucune formule existante pour ce champ'
                        }
                        secondaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: formulaTexts.study_description
                            ? 600
                            : 400,
                          color: formulaTexts.study_description
                            ? 'text.primary'
                            : 'text.secondary',
                          sx: { whiteSpace: 'pre-wrap' },
                        }}
                      />
                    </ListItem>
                  </>
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Nom étude
                            </Typography>
                            {formulaTexts.study_name && (
                              <NewTooltip
                                content={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {formulaTexts.study_name}
                                  </Typography>
                                }
                                placement="right"
                                light
                                width={800}
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  size="xs"
                                  style={{ cursor: 'help' }}
                                />
                              </NewTooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          formulaTexts.study_name
                            ? previewFields.study_name
                            : 'Aucune formule existante pour ce champ'
                        }
                        secondaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: formulaTexts.study_name ? 600 : 400,
                          color: formulaTexts.study_name
                            ? 'text.primary'
                            : 'text.secondary',
                          sx: { whiteSpace: 'pre-wrap' },
                        }}
                      />
                    </ListItem>
                  </>
                </List>
              )}
            </Card>
          </Box>

          {/* Main Form - Right Column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <form id="product-form" onSubmit={handleSubmit} noValidate>
              {formError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setFormError(null)}
                >
                  {formError}
                </Alert>
              )}

              {/* Typology and Subcategories section (no title) */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {/* Typology field */}
                  <Grid item xs={12} sm={6}>
                    <CustomAutocomplete
                      endpoint={(q) =>
                        `/products/v2/typologies/${
                          q ? `?search=${encodeURIComponent(q)}` : ''
                        }`
                      }
                      getOptionLabel={(option) => option.name || ''}
                      getOptionKey={(option) => option.slug}
                      isOptionEqualToValue={(o, v) => o.slug === v.slug}
                      name="typology_slug"
                      label="Typologie"
                      value={
                        typologies.find(
                          (t) => t.slug === formData.typology_slug,
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        handleInputChange({
                          target: {
                            name: 'typology_slug',
                            value: newValue ? newValue.slug : '',
                            type: 'text',
                          },
                        });
                      }}
                      required
                      disabled={formDisabled || !!(product && product.id)}
                      fullWidth
                      allowNull
                      error={!!fieldErrors.typology_slug}
                      helperText={
                        fieldErrors.typology_slug &&
                        fieldErrors.typology_slug[0]
                      }
                    />
                  </Grid>
                  {/* Subcategories field */}
                  <Grid item xs={12} sm={6}>
                    <MultiSelectAutocomplete
                      endpoint={(q) =>
                        `/products/v2/sub-categories/?${
                          formData.typology_slug
                            ? `typologies=${formData.typology_slug}&`
                            : ''
                        }${q ? `search=${encodeURIComponent(q)}` : ''}`
                      }
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(o, v) => o.slug === v.slug}
                      name="sub_categories"
                      label="Sous-catégories"
                      value={
                        Array.isArray(formData.sub_categories)
                          ? formData.sub_categories
                          : []
                      }
                      onChange={(e, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          sub_categories: newValue,
                        }));
                      }}
                      required={!!formData.is_validated}
                      disabled={formDisabled || !formData.typology_slug}
                      error={!!fieldErrors.sub_categories}
                      helperText={
                        fieldErrors.sub_categories &&
                        fieldErrors.sub_categories[0]
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* 1. Base information section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Informations de base
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="public_name"
                      label="Nom public"
                      value={
                        isGeneratedPublicName
                          ? decodeHtmlEntities(
                              generatedNames?.public_name || '',
                            )
                          : decodeHtmlEntities(formData.public_name)
                      }
                      onChange={handleInputChange}
                      fullWidth
                      required={
                        !!formData.is_validated && !formulaTexts.public_name
                      }
                      disabled={
                        formDisabled ||
                        (formData.is_validated && formulas?.public_name) ||
                        !formData.typology_slug
                      }
                      error={
                        isGeneratedPublicName &&
                        (isSavingProduct || isGeneratedPublicName)
                          ? false
                          : !!fieldErrors.public_name
                      }
                      helperText={
                        isGeneratedPublicName
                          ? 'Ce champ est généré automatiquement par une formule.'
                          : (fieldErrors.public_name &&
                              fieldErrors.public_name[0]) ||
                            ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="internal_name"
                      label="Nom interne"
                      value={
                        isGeneratedInternalName
                          ? decodeHtmlEntities(
                              generatedNames?.internal_name || '',
                            )
                          : decodeHtmlEntities(formData.internal_name)
                      }
                      onChange={handleInputChange}
                      fullWidth
                      required={
                        !!formData.is_validated && !formulaTexts.internal_name
                      }
                      disabled={
                        formDisabled ||
                        (formData.is_validated && formulas?.internal_name) ||
                        !formData.typology_slug
                      }
                      error={
                        isGeneratedInternalName &&
                        (isSavingProduct || isGeneratedInternalName)
                          ? false
                          : !!fieldErrors.internal_name
                      }
                      helperText={`Préfixe : ${
                        previewFields.prefix_internal_name ||
                        generatedNames.prefix_internal_name ||
                        ''
                      }`}
                    />
                  </Grid>
                  <Grid item xs={11} ml={1}>
                    <FormControlLabel
                      key="force_quote_description"
                      control={
                        <Switch
                          checked={Boolean(formData.force_quote_description)}
                          disabled={
                            formDisabled ||
                            (!formulas?.quote_description &&
                              !formData.force_quote_description &&
                              !kitLines.length)
                          }
                          onChange={(e) =>
                            handleInputChange({
                              target: {
                                name: 'force_quote_description',
                                value: e.target.checked,
                              },
                            })
                          }
                          name="force_quote_description"
                        />
                      }
                      label="Forcer la description du devis"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="quote_description"
                      label="Description devis"
                      value={(() => {
                        let value = '';
                        if (formData.force_quote_description) {
                          value = formData.quote_description;
                        } else if (kitLines.length > 0) {
                          value =
                            typeof calculatedDescription === 'string'
                              ? calculatedDescription
                              : '';
                        } else if (isGeneratedQuoteDescription) {
                          value = generatedNames?.quote_description || '';
                        } else {
                          value = formData.quote_description;
                        }
                        return decodeHtmlEntities(value);
                      })()}
                      onChange={handleInputChange}
                      fullWidth
                      required={
                        !!formData.is_validated &&
                        !formulaTexts.quote_description &&
                        formData.typology_slug !== 'accessories'
                      }
                      multiline
                      minRows={3}
                      InputProps={{ sx: { resize: 'vertical' } }}
                      disabled={
                        formDisabled ||
                        (formData.typology_slug &&
                          isGeneratedQuoteDescription &&
                          !formData.force_quote_description)
                      }
                      error={
                        formData.force_quote_description &&
                        !!fieldErrors.custom_quote_description
                      }
                      helperText={
                        !formData.force_quote_description &&
                        isGeneratedQuoteDescription
                          ? 'Ce champ est généré automatiquement. Activez "Forcer la description du devis" pour le personnaliser.'
                          : (fieldErrors.custom_quote_description &&
                              fieldErrors.custom_quote_description[0]) ||
                            ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="visual_name"
                      label="Nom visuel"
                      value={decodeHtmlEntities(formData.visual_name)}
                      onChange={handleInputChange}
                      fullWidth
                      disabled={formDisabled || !formData.typology_slug}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="study_description"
                      label="Description étude"
                      value={
                        isGeneratedStudyDescription
                          ? decodeHtmlEntities(
                              generatedNames?.study_description || '',
                            )
                          : decodeHtmlEntities(formData.study_description)
                      }
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      minRows={3}
                      InputProps={{ sx: { resize: 'vertical' } }}
                      disabled={
                        formDisabled ||
                        (formData.is_validated &&
                          formulas?.study_description) ||
                        !formData.typology_slug
                      }
                      error={
                        isGeneratedStudyDescription &&
                        (isSavingProduct || isGeneratedStudyDescription)
                          ? false
                          : !!fieldErrors.study_description
                      }
                      helperText={
                        isGeneratedStudyDescription
                          ? 'Ce champ est généré automatiquement par une formule.'
                          : (fieldErrors.study_description &&
                              fieldErrors.study_description[0]) ||
                            ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomAutocomplete
                      endpoint={(q) =>
                        `/products/v2/brands/${
                          q ? `?search=${encodeURIComponent(q)}` : ''
                        }`
                      }
                      getOptionLabel={(option) => option.name || ''}
                      getOptionKey={(option) => option.slug}
                      isOptionEqualToValue={(o, v) => o.slug === v.slug}
                      name="brand"
                      label="Marque"
                      value={formData.brand}
                      onChange={(e, newValue) => {
                        setFormData((prev) => ({ ...prev, brand: newValue }));
                      }}
                      required={false}
                      disabled={formDisabled || !formData.typology_slug}
                      fullWidth
                      allowNull
                      error={!!fieldErrors.brand}
                      helperText={fieldErrors.brand && fieldErrors.brand[0]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="study_name"
                      label="Nom étude"
                      value={
                        isGeneratedStudyName
                          ? decodeHtmlEntities(generatedNames?.study_name || '')
                          : decodeHtmlEntities(formData.study_name)
                      }
                      onChange={handleInputChange}
                      fullWidth
                      disabled={
                        formDisabled ||
                        (formData.is_validated && formulas?.study_name) ||
                        !formData.typology_slug
                      }
                      error={
                        isGeneratedStudyName &&
                        (isSavingProduct || isGeneratedStudyName)
                          ? false
                          : !!fieldErrors.study_name
                      }
                      helperText={
                        isGeneratedStudyName
                          ? 'Ce champ est généré automatiquement par une formule.'
                          : (fieldErrors.study_name &&
                              fieldErrors.study_name[0]) ||
                            ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="internal_note"
                      label="Note interne"
                      value={decodeHtmlEntities(formData.internal_note || '')}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      minRows={3}
                      InputProps={{ sx: { resize: 'vertical' } }}
                      disabled={formDisabled || !formData.typology_slug}
                      error={!!fieldErrors.internal_note}
                      helperText={
                        (fieldErrors.internal_note &&
                          fieldErrors.internal_note[0]) ||
                        'Note visible uniquement en interne'
                      }
                    />
                  </Grid>
                </Grid>
                {!formData.typology_slug && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'all',
                    }}
                  >
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      sx={{ textAlign: 'center' }}
                    >
                      Veuillez saisir une typologie
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Price section */}
              {!readOnly ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Prix fournisseur
                  </Typography>
                  <Card sx={{ mb: 1, p: 2 }}>
                    <Grid container spacing={2}>
                      {isKit && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mb: 1 }}>
                            <AlertTitle>
                              Comment le prix du kit est-il calculé ?
                            </AlertTitle>
                            Si <b>Prix fournisseur</b> est <b>0</b>, le prix du
                            kit est la somme des prix de ses lignes (chaque
                            ligne = quantité × prix du produit).
                            <br />
                            Si vous souhaitez <b>forcer le prix du kit</b>,
                            indiquez un <b>Prix fournisseur</b> non nul : le
                            prix du kit sera alors calculé selon la formule du
                            kit (par défaut : Prix fournisseur × Marge
                            typologie).
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="supplier_price"
                          label="Prix fournisseur (€)"
                          type="number"
                          value={formData.supplier_price}
                          onChange={handlePriceChange}
                          InputProps={{
                            startAdornment: <Box sx={{ mr: 0.5 }}>€</Box>,
                          }}
                          disabled={formDisabled || !formData.typology_slug}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Prix calculé
                          </Typography>
                          {/* Kit price logic: if product is a kit, show kit price (sum or formula) */}
                          {isKit ? (
                            <Tooltip
                              title={
                                calculatedPrice !== null &&
                                !Number.isNaN(calculatedPrice) ? (
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: 'max-content auto',
                                      gap: 0.5,
                                    }}
                                  >
                                    {getVatPrices(calculatedPrice).map(
                                      ({ rate, price }) => (
                                        <React.Fragment key={rate}>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 500,
                                              pr: 0.5,
                                              textAlign: 'right',
                                            }}
                                          >
                                            TVA {vatFormatter(rate)}% :
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 400 }}
                                          >
                                            {moneyFormatter(price)} € TTC
                                          </Typography>
                                        </React.Fragment>
                                      ),
                                    )}
                                  </Box>
                                ) : (
                                  '—'
                                )
                              }
                              placement="right"
                              arrow
                            >
                              <Typography
                                variant="h5"
                                color="primary"
                                sx={{
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'inline-block',
                                }}
                              >
                                {calculatedPrice !== null
                                  ? `${moneyFormatter(calculatedPrice)} € HT`
                                  : '—'}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                calculatedPrice !== null &&
                                !Number.isNaN(calculatedPrice) ? (
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: 'max-content auto',
                                      gap: 0.5,
                                    }}
                                  >
                                    {getVatPrices(calculatedPrice).map(
                                      ({ rate, price }) => (
                                        <React.Fragment key={rate}>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 500,
                                              pr: 0.5,
                                              textAlign: 'right',
                                            }}
                                          >
                                            TVA {vatFormatter(rate)}% :
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 400 }}
                                          >
                                            {moneyFormatter(price)} € TTC
                                          </Typography>
                                        </React.Fragment>
                                      ),
                                    )}
                                  </Box>
                                ) : (
                                  '—'
                                )
                              }
                              placement="right"
                              arrow
                            >
                              <Typography
                                variant="h5"
                                color="primary"
                                sx={{
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'inline-block',
                                }}
                              >
                                {calculatedPrice !== null
                                  ? `${moneyFormatter(calculatedPrice)} € HT`
                                  : '—'}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                      {priceFormula && (
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            Formule de prix :{' '}
                            <Box
                              component="code"
                              sx={{
                                backgroundColor: 'grey.100',
                                padding: '2px 4px',
                                borderRadius: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                border: '1px solid',
                                borderColor: 'grey.300',
                              }}
                            >
                              {priceFormula}
                            </Box>
                          </Typography>
                          {typologyMargin !== null && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              Marge typologie :{' '}
                              {vatFormatter((typologyMargin - 1) * 100)}%
                            </Typography>
                          )}
                          {/* Helper text for custom price formula */}
                          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                            Pour une formule spécifique à ce produit, éditez-la
                            ici :
                          </Typography>
                          {/* Warning if product price_formula is set */}
                          {formData.price_formula && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              Une formule de prix est définie sur ce produit.
                              Elle <b>remplacera</b> la formule de la typologie
                              lors du calcul.
                            </Alert>
                          )}
                          <TextField
                            name="price_formula"
                            label="Formule de prix personnalisée"
                            value={formData.price_formula || ''}
                            onChange={handleInputChange}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            disabled={formDisabled}
                            placeholder="Exemple : {{product.supplier_price}} * {{product.typology.margin}}"
                            helperText="Laissez vide pour utiliser la formule de la typologie. Utilisez les variables {{product.supplier_price}}, {{product.typology.margin}}, etc."
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </>
              ) : (
                <Box sx={{ mb: 3, mt: 3 }}>
                  <Grid container spacing={2}>
                    {isFranchiseeUser && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6" gutterBottom>
                          Prix fournisseur
                        </Typography>
                        <NewTooltip
                          content={
                            hasValidSupplierPrice ? (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'max-content auto',
                                  gap: 0.5,
                                }}
                              >
                                {getVatPrices(displaySupplierPrice).map(
                                  ({ rate, price }) => (
                                    <React.Fragment key={rate}>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 500,
                                          pr: 0.5,
                                          textAlign: 'right',
                                        }}
                                      >
                                        TVA {vatFormatter(rate)}% :
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        {moneyFormatter(price)} € TTC
                                      </Typography>
                                    </React.Fragment>
                                  ),
                                )}
                              </Box>
                            ) : (
                              '—'
                            )
                          }
                          placement="right"
                        >
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-block',
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 2,
                              borderRadius: 1,
                              minWidth: 200,
                            }}
                          >
                            {hasValidSupplierPrice
                              ? `${moneyFormatter(displaySupplierPrice)} € HT`
                              : '—'}
                          </Typography>
                        </NewTooltip>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="h6" gutterBottom>
                        Prix de vente
                      </Typography>
                      <NewTooltip
                        content={
                          calculatedPrice !== null &&
                          !Number.isNaN(calculatedPrice) ? (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'max-content auto',
                                gap: 0.5,
                              }}
                            >
                              {getVatPrices(calculatedPrice).map(
                                ({ rate, price }) => (
                                  <React.Fragment key={rate}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 500,
                                        pr: 0.5,
                                        textAlign: 'right',
                                      }}
                                    >
                                      TVA {vatFormatter(rate)}% :
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 400 }}
                                    >
                                      {moneyFormatter(price)} € TTC
                                    </Typography>
                                  </React.Fragment>
                                ),
                              )}
                            </Box>
                          ) : (
                            '—'
                          )
                        }
                        placement="right"
                      >
                        <Typography
                          variant="h5"
                          color="primary"
                          sx={{
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-block',
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2,
                            borderRadius: 1,
                            minWidth: 200,
                          }}
                        >
                          {calculatedPrice !== null
                            ? `${moneyFormatter(calculatedPrice)} € HT`
                            : '—'}
                        </Typography>
                      </NewTooltip>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Image section */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  Image
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <label htmlFor="product-image-upload">
                    <input
                      style={{ display: 'none' }}
                      id="product-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setImageFile(file);
                        setImagePreview(
                          file
                            ? URL.createObjectURL(file)
                            : product?.image_url || product?.image || null,
                        );
                      }}
                    />
                    <Button
                      variant="outlined"
                      disabled={formDisabled}
                      color="primary"
                      component="span"
                      startIcon={<FontAwesomeIcon icon={faCamera} />}
                      sx={{
                        height: 48,
                        fontWeight: 600,
                        fontSize: 16,
                        mb: 2,
                      }}
                    >
                      {product?.image_url || product?.image
                        ? "Remplacer l'image"
                        : 'Ajouter une image'}
                    </Button>
                  </label>
                  {imagePreview && (
                    <Card sx={{ maxWidth: 180, maxHeight: 180, boxShadow: 2 }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image={imagePreview}
                        alt="Product"
                        sx={{ objectFit: 'contain', maxHeight: 180 }}
                      />
                    </Card>
                  )}
                </Box>
              </Grid>

              {/* Data Sheets section */}
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  Fiches techniques
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        onClick={() => setDataSheetBrowserOpen(true)}
                        sx={{ height: 48, fontWeight: 600, fontSize: 16 }}
                        disabled={formDisabled}
                      >
                        Sélectionner une fiche technique
                      </Button>
                    </Grid>
                  </Grid>

                  {dataSheets.map((sheet, index) => (
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      key={sheet.id}
                      sx={{ mb: 2 }}
                    >
                      <Grid item xs>
                        <Grid container spacing={1}>
                          <Grid item xs={sheet.id ? true : 12}>
                            <TextField
                              label="Nom de la fiche"
                              value={sheet.name || ''}
                              onChange={(e) =>
                                handleDataSheetChange(
                                  index,
                                  'name',
                                  e.target.value,
                                )
                              }
                              fullWidth
                              size="small"
                              disabled={formDisabled}
                            />
                          </Grid>
                          {sheet.id ? (
                            <Grid item xs={4}>
                              <Button
                                variant="outlined"
                                onClick={() => openDataSheet(sheet)}
                                disabled={formDisabled}
                                fullWidth
                                endIcon={
                                  <FontAwesomeIcon
                                    icon={faExternalLinkAlt}
                                    size="xs"
                                  />
                                }
                              >
                                Voir la fiche
                              </Button>
                            </Grid>
                          ) : (
                            <Grid item xs={12}>
                              <label htmlFor={`data-sheet-file-${index}`}>
                                <input
                                  style={{ display: 'none' }}
                                  id={`data-sheet-file-${index}`}
                                  type="file"
                                  accept=".pdf,.doc,.docx"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handleDataSheetFileChange(index, file);
                                      handleDataSheetChange(
                                        index,
                                        'file',
                                        file.name,
                                      );
                                    }
                                  }}
                                />
                                <Button
                                  variant="outlined"
                                  component="span"
                                  disabled={formDisabled}
                                  fullWidth
                                  sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textAlign: dataSheetFiles[index]
                                      ? 'left'
                                      : 'center',
                                    justifyContent: dataSheetFiles[index]
                                      ? 'flex-start'
                                      : 'center',
                                  }}
                                >
                                  {dataSheetFiles[index]
                                    ? `Fichier : ${dataSheetFiles[index].name}`
                                    : 'Choisir un fichier'}
                                </Button>
                              </label>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item>
                        <IconButton
                          onClick={() => removeDataSheet(index)}
                          disabled={formDisabled}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  {/* {dataSheets.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Aucune fiche technique ajoutée
                  </Typography>
                ) */}
                </Box>

                {/* Warranties section */}
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    Garanties
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FontAwesomeIcon icon={faSearch} />}
                          onClick={() => setWarrantyBrowserOpen(true)}
                          sx={{ height: 48, fontWeight: 600, fontSize: 16 }}
                          disabled={formDisabled}
                        >
                          Sélectionner une garantie
                        </Button>
                      </Grid>
                    </Grid>

                    {warranties.map((warranty, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={warranty.id}
                        sx={{ mb: 2 }}
                      >
                        <Grid item xs>
                          <Grid container spacing={1}>
                            <Grid item xs={warranty.id ? true : 12}>
                              <TextField
                                label="Nom de la garantie"
                                value={warranty.name || ''}
                                onChange={(e) =>
                                  handleWarrantyChange(
                                    index,
                                    'name',
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                                disabled={formDisabled}
                              />
                            </Grid>
                            {warranty.id ? (
                              <Grid item xs={4}>
                                <Button
                                  variant="outlined"
                                  onClick={() => openWarranty(warranty)}
                                  disabled={formDisabled}
                                  fullWidth
                                  endIcon={
                                    <FontAwesomeIcon
                                      icon={faExternalLinkAlt}
                                      size="xs"
                                    />
                                  }
                                >
                                  Voir la garantie
                                </Button>
                              </Grid>
                            ) : (
                              <Grid item xs={12}>
                                <label htmlFor={`warranty-file-${index}`}>
                                  <input
                                    style={{ display: 'none' }}
                                    id={`warranty-file-${index}`}
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleWarrantyFileChange(index, file);
                                        handleWarrantyChange(
                                          index,
                                          'file',
                                          file.name,
                                        );
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    component="span"
                                    disabled={formDisabled}
                                    fullWidth
                                    sx={{
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textAlign: warrantyFiles[index]
                                        ? 'left'
                                        : 'center',
                                      justifyContent: warrantyFiles[index]
                                        ? 'flex-start'
                                        : 'center',
                                    }}
                                  >
                                    {warrantyFiles[index]
                                      ? `Fichier : ${warrantyFiles[index].name}`
                                      : 'Choisir un fichier'}
                                  </Button>
                                </label>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item>
                          <IconButton
                            onClick={() => removeWarranty(index)}
                            disabled={formDisabled}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </Grid>

                {/* Conformity Certificates section */}
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    Certificats de conformité
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FontAwesomeIcon icon={faSearch} />}
                          onClick={() =>
                            setConformityCertificateBrowserOpen(true)
                          }
                          sx={{ height: 48, fontWeight: 600, fontSize: 16 }}
                          disabled={formDisabled}
                        >
                          Sélectionner un certificat
                        </Button>
                      </Grid>
                    </Grid>

                    {conformityCertificates.map((cert, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={cert.id}
                        sx={{ mb: 2 }}
                      >
                        <Grid item xs>
                          <Grid container spacing={1}>
                            <Grid item xs={cert.id ? true : 12}>
                              <TextField
                                label="Nom du certificat"
                                value={cert.name || ''}
                                onChange={(e) =>
                                  handleConformityCertificateChange(
                                    index,
                                    'name',
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                                disabled={formDisabled}
                              />
                            </Grid>
                            {cert.id ? (
                              <Grid item xs={4}>
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    openConformityCertificate(cert)
                                  }
                                  disabled={formDisabled}
                                  fullWidth
                                  endIcon={
                                    <FontAwesomeIcon
                                      icon={faExternalLinkAlt}
                                      size="xs"
                                    />
                                  }
                                >
                                  Voir le certificat
                                </Button>
                              </Grid>
                            ) : (
                              <Grid item xs={12}>
                                <label
                                  htmlFor={`conformity-certificate-file-${index}`}
                                >
                                  <input
                                    style={{ display: 'none' }}
                                    id={`conformity-certificate-file-${index}`}
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleConformityCertificateFileChange(
                                          index,
                                          file,
                                        );
                                        handleConformityCertificateChange(
                                          index,
                                          'file',
                                          file.name,
                                        );
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    component="span"
                                    disabled={formDisabled}
                                    fullWidth
                                    sx={{
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textAlign: conformityCertificateFiles[
                                        index
                                      ]
                                        ? 'left'
                                        : 'center',
                                      justifyContent:
                                        conformityCertificateFiles[index]
                                          ? 'flex-start'
                                          : 'center',
                                    }}
                                  >
                                    {conformityCertificateFiles[index]
                                      ? `Fichier : ${conformityCertificateFiles[index].name}`
                                      : 'Choisir un fichier'}
                                  </Button>
                                </label>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item>
                          <IconButton
                            onClick={() => removeConformityCertificate(index)}
                            disabled={formDisabled}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </Grid>

                {/* User Manual section */}
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    Manuel utilisateur
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FontAwesomeIcon icon={faSearch} />}
                          onClick={() => setUserManualBrowserOpen(true)}
                          sx={{ height: 48, fontWeight: 600, fontSize: 16 }}
                          disabled={formDisabled}
                        >
                          Sélectionner un manuel
                        </Button>
                      </Grid>
                    </Grid>

                    {userManuals.map((manual, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={manual.id}
                        sx={{ mb: 2 }}
                      >
                        <Grid item xs>
                          <Grid container spacing={1}>
                            <Grid item xs={manual.id ? true : 12}>
                              <TextField
                                label="Nom du manuel"
                                value={manual.name || ''}
                                onChange={(e) =>
                                  handleUserManualChange(
                                    index,
                                    'name',
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                                disabled={formDisabled}
                              />
                            </Grid>
                            {manual.id ? (
                              <Grid item xs={4}>
                                <Button
                                  variant="outlined"
                                  onClick={() => openUserManual(manual)}
                                  disabled={formDisabled}
                                  fullWidth
                                  endIcon={
                                    <FontAwesomeIcon
                                      icon={faExternalLinkAlt}
                                      size="xs"
                                    />
                                  }
                                >
                                  Voir le manuel
                                </Button>
                              </Grid>
                            ) : (
                              <Grid item xs={12}>
                                <label htmlFor={`user-manual-file-${index}`}>
                                  <input
                                    style={{ display: 'none' }}
                                    id={`user-manual-file-${index}`}
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleUserManualFileChange(index, file);
                                        handleUserManualChange(
                                          index,
                                          'file',
                                          file.name,
                                        );
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    component="span"
                                    disabled={formDisabled}
                                    fullWidth
                                    sx={{
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textAlign: userManualFiles[index]
                                        ? 'left'
                                        : 'center',
                                      justifyContent: userManualFiles[index]
                                        ? 'flex-start'
                                        : 'center',
                                    }}
                                  >
                                    {userManualFiles[index]
                                      ? `Fichier : ${userManualFiles[index].name}`
                                      : 'Choisir un fichier'}
                                  </Button>
                                </label>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item>
                          <IconButton
                            onClick={() => removeUserManual(index)}
                            disabled={formDisabled}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* 3. Details section */}
              {formData.typology_slug &&
                !loadingDetailsSchema &&
                detailsSchema && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      Détails
                    </Typography>
                    <Grid container spacing={2} sx={{ pl: 0.5, pr: 0.5 }}>
                      {/* All details fields go here, as before */}
                      {detailsSchema.fields &&
                      detailsSchema.fields.length > 0 ? (
                        detailsSchema.fields.map((fieldSchema) => (
                          <Grid
                            item
                            xs={12}
                            sm={fieldSchema.fieldType === 'text' ? 12 : 6}
                            key={fieldSchema.name}
                            sx={{ alignContent: 'center' }}
                          >
                            {renderDynamicDetailField(fieldSchema)}
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            Aucun champ spécifique défini pour cette typologie.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              {loadingDetailsSchema && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100px',
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography sx={{ ml: 2 }}>
                    Chargement des champs détaillés...
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Lignes du kit
                </Typography>
                {kitLines.sort(sortBy('order')).map((line, idx) => (
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    key={line.id}
                    sx={{ mb: 2 }}
                  >
                    <Grid
                      item
                      xs={1}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={() => moveKitLineUp(idx)}
                        disabled={formDisabled || idx === 0}
                        size="medium"
                        style={{ paddingBottom: '2px' }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronUp}
                          size="md"
                          color={idx === 0 ? '#ccc' : undefined}
                        />
                      </IconButton>
                      <IconButton
                        onClick={() => moveKitLineDown(idx)}
                        disabled={formDisabled || idx === kitLines.length - 1}
                        size="medium"
                        style={{ paddingTop: '2px' }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          size="md"
                          color={
                            idx === kitLines.length - 1 ? '#ccc' : undefined
                          }
                        />
                      </IconButton>
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      sx={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <Tooltip
                        title={
                          line?.show_on_quote
                            ? 'Masquer du devis'
                            : 'Afficher sur le devis'
                        }
                      >
                        <IconButton
                          onClick={() => {
                            setKitLines((prevKitLines) =>
                              prevKitLines.map((kitLine, i) =>
                                i === idx
                                  ? {
                                      ...kitLine,
                                      show_on_quote: !kitLine.show_on_quote,
                                    }
                                  : kitLine,
                              ),
                            );
                          }}
                          disabled={formDisabled}
                        >
                          <FontAwesomeIcon
                            color={line?.show_on_quote ? 'green' : 'red'}
                            icon={line?.show_on_quote ? faEye : faEyeSlash}
                          />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={5}>
                      <ProductSearchAutocomplete
                        defaultValue={line.product || null}
                        onChange={(newValue) => {
                          setKitLines((prevKitLines) =>
                            prevKitLines
                              .sort(sortBy('order'))
                              .map((kitLine, i) =>
                                i === idx
                                  ? { ...kitLine, product: newValue }
                                  : kitLine,
                              ),
                          );
                          if (newValue?.id) {
                            mergeDocumentsFromKitChildProduct(idx, newValue.id);
                          }
                        }}
                        label="Produit du kit"
                        disabled={formDisabled || !formData.typology_slug}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        name={`kit_quantity_${idx}`}
                        label="Quantité"
                        type="number"
                        value={line.quantity}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setKitLines((prevKitLines) =>
                            prevKitLines
                              .sort(sortBy('order'))
                              .map((kitLine, i) =>
                                i === idx
                                  ? { ...kitLine, quantity: value }
                                  : kitLine,
                              ),
                          );
                        }}
                        required
                        disabled={formDisabled || !formData.typology_slug}
                        fullWidth
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      {(() => {
                        if (kitLineLoading[idx]) {
                          return <CircularProgress size={20} />;
                        }
                        if (kitLineErrors[idx]) {
                          return (
                            <Typography color="error" variant="body2">
                              {kitLineErrors[idx]}
                            </Typography>
                          );
                        }
                        if (
                          kitLinePrices[idx] !== undefined &&
                          kitLinePrices[idx] !== null
                        ) {
                          return (
                            <NewTooltip
                              content={
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'max-content auto',
                                    gap: 0.5,
                                  }}
                                >
                                  {getVatPrices(Number(kitLinePrices[idx])).map(
                                    ({ rate, price }) => (
                                      <React.Fragment key={rate}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            pr: 0.5,
                                            textAlign: 'right',
                                          }}
                                        >
                                          TVA {vatFormatter(rate)}% :
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 400 }}
                                        >
                                          {moneyFormatter(price)} € TTC
                                        </Typography>
                                      </React.Fragment>
                                    ),
                                  )}
                                </Box>
                              }
                              placement="right"
                              arrow
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, cursor: 'pointer' }}
                                align="right"
                              >
                                {moneyFormatter(Number(kitLinePrices[idx]))} €
                                HT
                              </Typography>
                            </NewTooltip>
                          );
                        }
                        return (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="right"
                          >
                            —
                          </Typography>
                        );
                      })()}
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        onClick={() => {
                          setKitLines(
                            kitLines
                              .sort(sortBy('order'))
                              .filter((_, i) => i !== idx),
                          );
                        }}
                        disabled={formDisabled}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                {kitLines.length > 0 && kitLinesPriceTotal && (
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <Grid item xs={1} />
                    <Grid item xs={1} />
                    <Grid item xs={5} />
                    <Grid item xs={2} />
                    <Grid item xs={2}>
                      {(() => {
                        const t = kitLinesPriceTotal;
                        if (t.status === 'loading') {
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <CircularProgress size={20} />
                            </Box>
                          );
                        }
                        if (t.status === 'incomplete') {
                          return (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="right"
                            >
                              —
                            </Typography>
                          );
                        }
                        return (
                          <NewTooltip
                            content={
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'max-content auto',
                                  gap: 0.5,
                                }}
                              >
                                {getVatPrices(Number(t.total)).map(
                                  ({ rate, price }) => (
                                    <React.Fragment key={rate}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          pr: 0.5,
                                          textAlign: 'right',
                                        }}
                                      >
                                        TVA {vatFormatter(rate)}% :
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 400 }}
                                      >
                                        {moneyFormatter(price)} € TTC
                                      </Typography>
                                    </React.Fragment>
                                  ),
                                )}
                              </Box>
                            }
                            placement="right"
                            arrow
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, cursor: 'pointer' }}
                              align="right"
                            >
                              Total : {moneyFormatter(Number(t.total))} € HT
                            </Typography>
                          </NewTooltip>
                        );
                      })()}
                    </Grid>
                    <Grid item xs={1} />
                  </Grid>
                )}
                <Button
                  variant="outlined"
                  disabled={formDisabled}
                  onClick={() =>
                    setKitLines([
                      ...kitLines.sort(sortBy('order')),
                      {
                        product: null,
                        quantity: 1,
                        show_on_quote: true,
                        order: kitLines.length + 1,
                      },
                    ])
                  }
                  sx={{ mt: 2 }}
                >
                  Ajouter un produit au kit
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
        {showKitsTab && (
          <Box
            sx={{
              display: productDialogTab === 'kits' ? 'block' : 'none',
            }}
          >
            <ProductContainingKitsPanel
              key={product.id}
              productId={product.id}
              productName={containingKitProductName}
              writesDisabled={
                readOnly || !(user?.is_superuser || user?.is_product_manager)
              }
              onCountChange={handleContainingKitsCount}
              onSyncingChange={setContainingKitsSyncing}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {showKitsTab && productDialogTab === 'kits' ? (
          <Button
            variant="contained"
            onClick={handleDialogClose}
            disabled={containingKitsSyncing}
          >
            Fermer
          </Button>
        ) : readOnly ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: onStartEdit ? 'space-between' : 'flex-end',
              gap: 1,
              width: '100%',
            }}
          >
            {onStartEdit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                onClick={onStartEdit}
              >
                Editer le produit
              </Button>
            )}
            <Button variant="contained" onClick={handleDialogClose}>
              Fermer
            </Button>
          </Box>
        ) : (
          <Grid container justifyContent="space-between">
            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleDialogClose}
                    disabled={formDisabled || containingKitsSyncing}
                  >
                    Annuler
                  </Button>
                </Grid>
                <Grid item>
                  {product && product.id && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleDuplicate}
                      disabled={formDisabled || loadingTaxonomies}
                      sx={{ mr: 'auto' }}
                    >
                      {isSavingProduct && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      Dupliquer
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {product && product.id && product.is_validated && (
              <Grid item xs={2} sx={{ alignContent: 'center' }}>
                <Chip
                  variant="filled"
                  color="primary"
                  label={`Odoo ID : ${odooChipLabelId}`}
                  deleteIcon={
                    <span style={{ color: 'black' }}>
                      <FontAwesomeIcon icon={faSync} spin={isSavingProduct} />
                    </span>
                  }
                  onDelete={
                    (user?.is_superuser || user?.is_product_manager_admin) &&
                    !isSavingProduct
                      ? handleSyncToOdoo
                      : undefined
                  }
                />
              </Grid>
            )}
            <Grid item>
              <Button
                type="submit"
                form="product-form"
                variant="contained"
                color="primary"
                disabled={formDisabled || loadingTaxonomies}
              >
                {isSavingProduct && <CircularProgress size={24} />}
                {!isSavingProduct && product && product.id && 'Mettre à jour'}
                {!isSavingProduct && (!product || !product.id) && 'Créer'}
              </Button>
            </Grid>
          </Grid>
        )}
      </DialogActions>

      {/* Data Sheet Browser Dialog */}
      <FileBrowserDialog
        open={dataSheetBrowserOpen}
        onClose={() => setDataSheetBrowserOpen(false)}
        onSelect={handleDataSheetBrowserSelect}
        existingFiles={dataSheets}
        endpoint="/products/v2/datasheets/"
        title="Sélectionner les fiches techniques"
        subtitle="Parcourez les fiches existantes ou ajoutez-en de nouvelles"
        searchPlaceholder="Rechercher des fiches techniques..."
        emptyMessage="Aucune fiche technique trouvée"
        nameLabel="Nom de la fiche"
        namePlaceholder="Ex: Fiche technique panneau solaire"
        uploadButtonLabel="Ajouter une nouvelle fiche technique"
        uploadingLabel="Upload en cours..."
        errorLoadingMessage="Erreur lors du chargement des fiches techniques"
        errorUploadMessage="Erreur lors de l'upload de la fiche technique"
        attachButtonLabel="Ajouter la fiche"
        canViewFiles
        brandId={formData.brand?.id}
      />

      {/* Warranty Browser Dialog */}
      <FileBrowserDialog
        open={warrantyBrowserOpen}
        onClose={() => setWarrantyBrowserOpen(false)}
        onSelect={handleWarrantyBrowserSelect}
        existingFiles={warranties}
        endpoint="/products/v2/warranties/"
        title="Sélectionner les garanties"
        subtitle="Parcourez les garanties existantes ou ajoutez-en de nouvelles"
        searchPlaceholder="Rechercher des garanties..."
        emptyMessage="Aucune garantie trouvée"
        nameLabel="Nom de la garantie"
        namePlaceholder="Ex: Garantie panneau solaire 25 ans"
        uploadButtonLabel="Ajouter une nouvelle garantie"
        uploadingLabel="Upload en cours..."
        errorLoadingMessage="Erreur lors du chargement des garanties"
        errorUploadMessage="Erreur lors de l'upload de la garantie"
        attachButtonLabel="Ajouter la garantie"
        canViewFiles
        brandId={formData.brand?.id}
      />

      {/* Conformity Certificate Browser Dialog */}
      <FileBrowserDialog
        open={conformityCertificateBrowserOpen}
        onClose={() => setConformityCertificateBrowserOpen(false)}
        onSelect={handleConformityCertificateBrowserSelect}
        existingFiles={conformityCertificates}
        endpoint="/products/v2/conformity-certificates/"
        title="Sélectionner les certificats de conformité"
        subtitle="Parcourez les certificats existants ou ajoutez-en de nouveaux"
        searchPlaceholder="Rechercher des certificats..."
        emptyMessage="Aucun certificat de conformité trouvé"
        nameLabel="Nom du certificat"
        namePlaceholder="Ex: Certificat CE panneau solaire"
        uploadButtonLabel="Ajouter un nouveau certificat"
        uploadingLabel="Upload en cours..."
        errorLoadingMessage="Erreur lors du chargement des certificats"
        errorUploadMessage="Erreur lors de l'upload du certificat"
        attachButtonLabel="Ajouter le certificat"
        canViewFiles
        brandId={formData.brand?.id}
      />

      {/* User Manual Browser Dialog */}
      <FileBrowserDialog
        open={userManualBrowserOpen}
        onClose={() => setUserManualBrowserOpen(false)}
        onSelect={handleUserManualBrowserSelect}
        existingFiles={userManuals}
        endpoint="/products/v2/user-manuals/"
        title="Sélectionner les manuels utilisateur"
        subtitle="Parcourez les manuels existants ou ajoutez-en de nouveaux"
        searchPlaceholder="Rechercher des manuels..."
        emptyMessage="Aucun manuel utilisateur trouvé"
        nameLabel="Nom du manuel"
        namePlaceholder="Ex: Manuel d'installation panneau solaire"
        uploadButtonLabel="Ajouter un nouveau manuel"
        uploadingLabel="Upload en cours..."
        errorLoadingMessage="Erreur lors du chargement des manuels"
        errorUploadMessage="Erreur lors de l'upload du manuel"
        attachButtonLabel="Ajouter le manuel"
        canViewFiles
        brandId={formData.brand?.id}
      />

      <Dialog
        open={yearlyDegradationDialogOpen}
        onClose={handleCloseYearlyDegradationDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Dégradation annuelle</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Capacité garantie"
            type="number"
            value={degradationDialogCapacity}
            onChange={(e) => setDegradationDialogCapacity(e.target.value)}
            fullWidth
            helperText="Pourcentage de capacité restante à la fin de la période (ex. 70 pour 70 %)"
            inputProps={{ step: '0.01' }}
          />
          <TextField
            margin="dense"
            label="Durée garantie performance"
            type="number"
            value={degradationDialogDuration}
            onChange={(e) => setDegradationDialogDuration(e.target.value)}
            fullWidth
            helperText="Années"
            inputProps={{ step: '0.01', min: '0.01' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseYearlyDegradationDialog}>Annuler</Button>
          <Button onClick={handleApplyYearlyDegradation} variant="contained">
            Valider
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

ProductForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  onStartEdit: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  product: PropTypes.shape({
    is_kit: PropTypes.bool,
    kit_lines: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.shape(),
        quantity: PropTypes.number,
      }),
    ),
    public_name: PropTypes.string,
    internal_name: PropTypes.string,
    visual_name: PropTypes.string,
    force_quote_description: PropTypes.bool,
    quote_description: PropTypes.string,
    internal_note: PropTypes.string,
    study_description: PropTypes.string,
    study_name: PropTypes.string,
    brand: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    supplier: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    supplier_price: PropTypes.number,
    can_be_bought: PropTypes.bool,
    can_be_sold: PropTypes.bool,
    is_active: PropTypes.bool,
    is_validated: PropTypes.bool,
    sub_categories: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    typology: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        slug: PropTypes.string,
        name: PropTypes.string,
        price_formula: PropTypes.string,
      }),
    ]),
    typology_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    details: PropTypes.shape({}),
    formulas: PropTypes.shape({}),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    image_url: PropTypes.string,
    price_formula: PropTypes.string,
    created_by: PropTypes.string,
    odoo_id: PropTypes.number,
    odoo_bom_id: PropTypes.number,
    data_sheets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        file: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    warranties: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        file: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    conformity_certificates: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        file: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    user_manuals: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        file: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }),
  isLoading: PropTypes.bool,
  handleDuplicateProduct: PropTypes.func,
  handleSyncProductToOdoo: PropTypes.func,
};

ProductForm.defaultProps = {
  product: null,
  isLoading: false,
  readOnly: false,
  handleDuplicateProduct: () => {},
  handleSyncProductToOdoo: () => {},
};

export default ProductForm;
